import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createRoot } from "react-dom/client";
import "@fontsource/oswald/latin-400.css";
import "@fontsource/oswald/latin-500.css";
import "@fontsource/oswald/latin-600.css";
import "@fontsource/oswald/latin-700.css";
import "@fontsource/jetbrains-mono/latin-400.css";
import "@fontsource/jetbrains-mono/latin-600.css";
import "@fontsource/jetbrains-mono/latin-800.css";
import { blankCareer, careerTotals, hasCareerActivity, nextAttributeCost, nextSkillCost, normalizeCareer } from "./career.js";
import { createLibraryBackup, libraryBackupFilename, parseLibraryBackup } from "./backup.js";
import { GOOGLE_DRIVE_AUTO_BACKUP, GOOGLE_DRIVE_FOLDER, GOOGLE_DRIVE_SCOPE, GoogleDriveBackupClient, googleDriveEnvironmentAvailable } from "./google-drive.js";

if (!window.runnerForgeStorage && window.storage) window.runnerForgeStorage = window.storage;
if (!window.runnerForgeStorage) {
  window.runnerForgeStorage = {
    label: "this browser",
    async get(k) { const v = localStorage.getItem(k); return v === null ? null : { key: k, value: v }; },
    async set(k, v) { localStorage.setItem(k, v); return { key: k, value: v }; },
    async delete(k) { localStorage.removeItem(k); return { key: k, deleted: true }; },
  };
}
const storageProvider = window.runnerForgeStorage;
const DEFAULT_GOOGLE_CLIENT_ID = "491846630747-rc9j65aj888nsdork2ldb1373510b8kk.apps.googleusercontent.com";
const GOOGLE_CLIENT_ID = String(window.RUNNER_FORGE_CONFIG?.googleClientId || DEFAULT_GOOGLE_CLIENT_ID).trim();

const Svg = ({ size = 16, children, fill = "none" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>{children}</svg>
);
const Plus = (p) => <Svg {...p}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></Svg>;
const X = (p) => <Svg {...p}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Svg>;
const Check = (p) => <Svg {...p}><polyline points="20 6 9 17 4 12" /></Svg>;
const ChevronDown = (p) => <Svg {...p}><polyline points="6 9 12 15 18 9" /></Svg>;
const Download = (p) => <Svg {...p}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></Svg>;
const Copy = (p) => <Svg {...p}><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></Svg>;
const Save = (p) => <Svg {...p}><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></Svg>;
const Trash2 = (p) => <Svg {...p}><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></Svg>;
const FilePlus = (p) => <Svg {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="12" y1="12" x2="12" y2="18" /><line x1="9" y1="15" x2="15" y2="15" /></Svg>;
const Printer = (p) => <Svg {...p}><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></Svg>;
const Cloud = (p) => <Svg {...p}><path d="M17.5 19H6a4 4 0 0 1-.8-7.92A6.5 6.5 0 0 1 17.67 9.2 5 5 0 0 1 17.5 19z" /></Svg>;
const HelpCircle = (p) => <Svg {...p}><circle cx="12" cy="12" r="10" /><path d="M9.1 9a3 3 0 1 1 5.83 1c0 2-3 2-3 4" /><line x1="12" y1="18" x2="12.01" y2="18" /></Svg>;
const ExternalLink = (p) => <Svg {...p}><path d="M15 3h6v6" /><path d="M10 14L21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></Svg>;
const Shield = (p) => <Svg {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></Svg>;
const AlertTriangle = (p) => <Svg {...p}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></Svg>;
const Zap = (p) => <Svg {...p} fill="currentColor"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></Svg>;
const Skull = (p) => <Svg {...p}><circle cx="9" cy="12" r="1" fill="currentColor" stroke="none" /><circle cx="15" cy="12" r="1" fill="currentColor" stroke="none" /><path d="M8 20v1a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-1" /><path d="M12 2a8 8 0 0 0-8 8c0 2.6 1.5 4.6 3 6h10c1.5-1.4 3-3.4 3-6a8 8 0 0 0-8-8z" /></Svg>;
const Dice5 = (p) => <Svg {...p}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8" cy="8" r="1.3" fill="currentColor" stroke="none" /><circle cx="16" cy="8" r="1.3" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="1.3" fill="currentColor" stroke="none" /><circle cx="8" cy="16" r="1.3" fill="currentColor" stroke="none" /><circle cx="16" cy="16" r="1.3" fill="currentColor" stroke="none" /></Svg>;


/* ============================================================================
   SHADOWRUN ANARCHY 2.0 — CHARACTER BUILDER
   Rules data pulled from the 2.0 rulebook (creation costs, skills, spells,
   adept powers, metatype caps, wound formula). Budget is a soft meter, not a
   hard gate — Anarchy costs are partly GM-adjudicated by design.
   ========================================================================== */

// ---- Rules data -----------------------------------------------------------

const METATYPES = {
  Human: { STR: 4, AGI: 4, WIL: 4, LOG: 4, CHA: 4, EDGE: 4, quality: "No racial Quality · Edge 4" },
  Elf:   { STR: 4, AGI: 4, WIL: 4, LOG: 4, CHA: 5, EDGE: 3, quality: "Racial Quality: low-light vision · Charisma max 5" },
  Dwarf: { STR: 4, AGI: 4, WIL: 5, LOG: 4, CHA: 4, EDGE: 3, quality: "Racial Quality: thermographic vision · Willpower max 5" },
  Ork:   { STR: 5, AGI: 4, WIL: 4, LOG: 4, CHA: 4, EDGE: 3, quality: "Racial Quality: low-light vision · Strength max 5" },
  Troll: { STR: 6, AGI: 4, WIL: 4, LOG: 4, CHA: 4, EDGE: 3, quality: "Racial Quality: thermographic vision · Strength max 6" },
};

const LEVELS = {
  Ganger:         { budget: 300000, skillMax: 4, attrsAtMax: 0, rrMax: { attribute: 1, skill: 1, specialization: 1 } },
  Runner:         { budget: 375000, skillMax: 5, attrsAtMax: 1, rrMax: { attribute: 1, skill: 1, specialization: 2 } },
  "Prime Runner": { budget: 450000, skillMax: 6, attrsAtMax: 2, rrMax: { attribute: 2, skill: 2, specialization: 2 } },
};

const ATTRS = ["STR", "AGI", "WIL", "LOG", "CHA", "EDGE"];
const BUY_ATTRS = ["STR", "AGI", "WIL", "LOG", "CHA"]; // Edge is set by metatype, not bought
const ATTR_LABEL = { STR: "Strength", AGI: "Agility", WIL: "Willpower", LOG: "Logic", CHA: "Charisma", EDGE: "Edge" };

const SKILLS = {
  Athletics:        { attrs: ["STR", "AGI"], specs: ["climbing", "parkour", "ranged defense", "running", "swimming"] },
  "Close Combat":   { attrs: ["AGI", "WIL"], specs: ["unarmed", "blades", "clubs", "defense", "astral combat"] },
  "Ranged Weapons": { attrs: ["AGI"], specs: ["throwing", "archery", "pistols", "SMGs", "rifles", "grenade launchers", "heavy weapons", "mounted weapons"] },
  Perception:       { attrs: ["LOG"], specs: ["astral", "Matrix", "physical", "social"] },
  Piloting:         { attrs: ["AGI"], specs: ["bikes", "cars", "trucks", "water vehicles", "flying vehicles", "ground drones", "water drones", "flying drones"] },
  Stealth:          { attrs: ["AGI", "LOG"], specs: ["astral sneaking", "Matrix sneaking", "physical sneaking", "palming", "lockpicking"] },
  Survival:         { attrs: ["LOG", "WIL", "CHA"], specs: ["animals", "composure", "first aid", "navigation", "wilderness"] },
  Cracking:         { attrs: ["LOG", "WIL"], specs: ["backdoor", "brute force", "cybercombat"], note: "needs a cyberdeck" },
  Electronics:      { attrs: ["LOG"], specs: ["Matrix protection", "Matrix search", "personal electronics"] },
  Engineering:      { attrs: ["LOG"], specs: ["cybernetics B&R", "drones B&R", "electronics B&R", "mechanical B&R", "vehicles B&R", "electronic warfare", "demolitions", "remote-controlled weapons"] },
  Influence:        { attrs: ["CHA"], specs: ["bluff", "etiquette", "impersonation", "intimidation", "negotiation"] },
  Network:          { attrs: ["CHA"], specs: ["academic", "corporate", "criminal", "engineering", "government", "magic", "Matrix", "media", "medical", "street"] },
  Conjuring:        { attrs: ["CHA", "LOG"], specs: ["spirits of air", "spirits of beasts", "spirits of earth", "spirits of fire", "spirits of kin", "spirits of plants", "spirits of water", "banishing"], awakened: true },
  Sorcery:          { attrs: ["LOG", "WIL"], specs: ["counterspelling", "combat spells", "detection spells", "health spells", "illusion spells", "manipulation spells"], awakened: true },
};

const AWAKENED_TYPES = {
  Mundane:                   { rating: 0, faculties: "No magic", sorcery: false, conjuring: false, adept: false },
  "Full Magician":           { rating: 5, faculties: "Astral perception & projection · Sorcery · Conjuring", sorcery: true, conjuring: true, adept: false },
  "Sorcerer (aspected)":     { rating: 2, faculties: "Astral perception · Sorcery only (no summoning)", sorcery: true, conjuring: false, adept: false },
  "Conjurer (aspected)":     { rating: 3, faculties: "Astral perception · Conjuring only (no spells)", sorcery: false, conjuring: true, adept: false },
  "Clairvoyant (aspected)":  { rating: 2, faculties: "Astral perception & projection only", sorcery: false, conjuring: false, adept: false },
  Adept:                     { rating: 1, faculties: "Adept powers · no spellcasting", sorcery: false, conjuring: false, adept: true },
  "Mystic Adept":            { rating: 4, faculties: "Sorcery · Conjuring · Adept powers (no astral by default)", sorcery: true, conjuring: true, adept: true },
};

const ADEPT_POWERS = {
  "Astral Armor":              { rating: 2, effect: "+1 armor vs spells and on the astral plane" },
  Armor:                       { rating: 2, effect: "+1 physical armor" },
  "Combat Sense":              { rating: 8, effect: "RR 1 on Close Combat and Athletics (ranged defense)" },
  "Danger Sense":              { rating: 1, effect: "Sense an imminent threat with no Perception test" },
  "Distance Strike":           { rating: 3, effect: "Unarmed attacks at short range (Disadvantage)" },
  "Elemental Strike":          { rating: 2, effect: "DV +1 unarmed + an elemental effect" },
  "Elemental Weapon":          { rating: 2, effect: "DV +1 melee weapon + an elemental effect" },
  "Empathic Sense":            { rating: 7, effect: "RR 3 on Perception (social)" },
  "Enhanced Perception":       { rating: 3, effect: "RR 1 Perception (physical) + a bonus sense per extra rating" },
  "Improved Ability":          { rating: 6, effect: "RR 1 on one chosen skill" },
  "Improved Attribute":        { rating: 11, effect: "RR 1 on one chosen attribute" },
  "Improved Reflexes":         { rating: 6, effect: "+1 action/narration · +1 Edge/scene · no cap on extra actions" },
  "Killing Hands":             { rating: 3, effect: "DV +2 unarmed · strikes hurt spirits" },
  "Light-footed":              { rating: 5, effect: "RR 2 on Stealth (physical sneaking)" },
  "Morphological & Vocal Control": { rating: 10, effect: "RR 1 Influence · RR 2 Influence (impersonation)" },
  "Mystic Armor":              { rating: 3, effect: "+1 armor against astral and physical damage" },
  "Pain Resistance":           { rating: 7, effect: "+1 serious wound box" },
  "Reflexes Boost":            { rating: 3, effect: "+1 Edge/scene · no cap on extra actions per turn" },
  "Vocal Control":             { rating: 8, effect: "RR 1 Influence · RR 1 Influence (impersonation)" },
  "Wall Running":              { rating: 1, effect: "Run along vertical surfaces" },
};

const SPELLS = {
  Combat:       ["Manabolt", "Stunbolt", "Blast", "Flamestrike", "Ice Spear", "Lightning Bolt", "Acid Stream"],
  Detection:    ["Analyze Magic", "Analyze Truth", "Mind Probe", "Borrow Senses", "Combat Sense", "Detect Enemies", "Detect Life", "Detect Magic"],
  Health:       ["Heal", "Increase Reflexes"],
  Illusion:     ["Chimera", "Confusion", "Invisibility", "Mask", "Silence", "Phantasm"],
  Manipulation: ["Control Thoughts", "Armor", "Astral Armor", "Mystic Armor", "Elemental Aura", "Levitate", "Physical Barrier", "Mana Barrier", "Dual Barrier", "Shaping", "Magic Fingers"],
};

const AMP_TYPES = {
  Quality:             { base: 0, essence: 0, baseCost: 0, note: "First Quality base 0; later Qualities base 3." },
  Cyberware:           { base: 1, essence: 1, baseCost: 0, note: "Each implant costs 1 Essence." },
  Bioware:             { base: 2, essence: 0.5, baseCost: 0, note: "Each implant costs 0.5 Essence." },
  Equipment:           { base: 0, essence: 0, baseCost: 2500, note: "Includes the required underlying equipment purchase." },
  Cyberdeck:           { base: 1, essence: 0, baseCost: 0, note: "Required to use Cracking." },
  "Vehicle or drone":  { base: 1, essence: 0, baseCost: 0, note: "Base rating supplies the vehicle or drone." },
  Contact:             { base: 0, essence: 0, baseCost: 0, note: "Named individual in the runner's network." },
};

const RR_SCOPE_COST = { specialization: 2, skill: 5, attribute: 10 };

const RISK_TABLE = [
  ["1", "2", "4", "6+"],
  ["3", "5", "7", "10+"],
  ["5", "8", "11", "13+"],
  ["8", "12", "15", "N/A"],
];

// ---- Costs (nuyen) --------------------------------------------------------

const attrCost = (v, max) => (v <= 0 ? 0 : v < max ? 10000 * v : 10000 * (v - 1) + 20000);
const skillCost = (r) => (r <= 0 ? 0 : 2500 * Math.min(r, 5) + 5000 * Math.max(0, r - 5));
const yen = (n) => "¥" + n.toLocaleString();

// ---- Build schema and migration ------------------------------------------

const SCHEMA_VERSION = 3;
const APP_VERSION = __RUNNER_FORGE_VERSION__;
const MAX_IMPORT_BYTES = 10 * 1024 * 1024;
const AUTO_BACKUP_DELAY_MS = 15_000;
const clamp = (n, min, max) => Math.min(max, Math.max(min, Number.isFinite(Number(n)) ? Number(n) : min));
const makeId = () => globalThis.crypto?.randomUUID?.() || `runner-${Date.now()}-${Math.random().toString(16).slice(2)}`;
const cleanText = (v, fallback = "") => typeof v === "string" ? v.slice(0, 5000) : fallback;
const fitArray = (value, length) => Array.from({ length }, (_, i) => cleanText(Array.isArray(value) ? value[i] : ""));

const blankBuild = () => ({
  schemaVersion: SCHEMA_VERSION, id: makeId(),
  name: "New Runner", streetname: "", metatype: "Human", level: "Runner", awakenedType: "Mundane",
  attributes: { STR: 1, AGI: 1, WIL: 1, LOG: 1, CHA: 1, EDGE: 4 },
  skills: [], knowledgeSkills: [], spells: [], adeptPowers: [], amps: [], weapons: [],
  armor: { name: "", rating: 0, bonus: 0 }, mentalProtection: 0, gear: [],
  keywords: ["", "", "", "", ""], dispositions: ["", "", "", ""], cues: ["", "", "", ""], bio: "",
  career: blankCareer(),
});

const normalizeBuild = (raw, { newIdentity = false } = {}) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) throw new Error("That file does not contain a character build.");
  if (Number(raw.schemaVersion) > SCHEMA_VERSION) throw new Error("This build was created by a newer version of Runner Forge.");
  const metatype = Object.hasOwn(METATYPES, raw.metatype) ? raw.metatype : "Human";
  const level = Object.hasOwn(LEVELS, raw.level) ? raw.level : "Runner";
  const awakenedType = Object.hasOwn(AWAKENED_TYPES, raw.awakenedType) ? raw.awakenedType : "Mundane";
  const meta = METATYPES[metatype];
  const attrs = {};
  BUY_ATTRS.forEach((k) => { attrs[k] = clamp(raw.attributes?.[k], 1, meta[k]); });
  attrs.EDGE = meta.EDGE;

  const skills = (Array.isArray(raw.skills) ? raw.skills : []).filter((s) => s && Object.hasOwn(SKILLS, s.name)).map((s) => {
    const def = SKILLS[s.name];
    const testAttr = def.attrs.includes(s.testAttr) ? s.testAttr : def.attrs.includes(s.attr) ? s.attr : def.attrs[0];
    const specs = [...new Set((Array.isArray(s.specs) ? s.specs : []).filter((sp) => def.specs.includes(sp)))];
    return { name: s.name, rating: clamp(s.rating, 0, 8), testAttr, specs };
  }).filter((s, i, all) => all.findIndex((x) => x.name === s.name) === i);

  const spells = (Array.isArray(raw.spells) ? raw.spells : []).filter((s) => s && typeof s.name === "string").map((s) => ({
    name: cleanText(s.name, "Unnamed spell").slice(0, 120),
    category: Object.hasOwn(SPELLS, s.category) ? s.category : "Custom",
  }));
  const adeptPowers = (Array.isArray(raw.adeptPowers) ? raw.adeptPowers : []).filter((p) => p && Object.hasOwn(ADEPT_POWERS, p.name)).map((p) => ({
    name: p.name, rating: clamp(p.rating ?? ADEPT_POWERS[p.name].rating, 1, 99),
  }));
  const amps = (Array.isArray(raw.amps) ? raw.amps : []).filter(Boolean).map((a) => {
    const inferredType = Number(a.essence) === 0.5 ? "Bioware" : Number(a.essence) > 0 ? "Cyberware" : "Quality";
    const type = Object.hasOwn(AMP_TYPES, a.type) ? a.type : inferredType;
    return {
      id: cleanText(a.id) || makeId(), name: cleanText(a.name, "Unnamed Amp").slice(0, 160), type,
      rating: clamp(a.rating, 0, 99), essence: AMP_TYPES[type].essence, baseCost: AMP_TYPES[type].baseCost,
      effect: cleanText(a.effect).slice(0, 1000),
      rrEffects: (Array.isArray(a.rrEffects) ? a.rrEffects : []).filter((r) => r && Object.hasOwn(RR_SCOPE_COST, r.scope)).map((r) => ({
        id: cleanText(r.id) || makeId(), scope: r.scope, target: cleanText(r.target, "Unspecified").slice(0, 120), level: clamp(r.level, 1, 3),
      })),
    };
  });

  return {
    schemaVersion: SCHEMA_VERSION, id: newIdentity ? makeId() : cleanText(raw.id) || makeId(),
    name: cleanText(raw.name, "New Runner").slice(0, 160), streetname: cleanText(raw.streetname).slice(0, 160),
    metatype, level, awakenedType, attributes: attrs, skills,
    knowledgeSkills: [...new Set((Array.isArray(raw.knowledgeSkills) ? raw.knowledgeSkills : []).map((x) => cleanText(x).slice(0, 160)).filter(Boolean))],
    spells, adeptPowers, amps,
    weapons: (Array.isArray(raw.weapons) ? raw.weapons : []).filter(Boolean).map((w) => ({
      name: cleanText(w.name, "Unnamed weapon").slice(0, 160), dv: cleanText(String(w.dv ?? "4")).slice(0, 30),
      range: cleanText(w.range, "OK/—/—/—").slice(0, 40), expert: Boolean(w.expert),
    })),
    armor: { name: cleanText(raw.armor?.name).slice(0, 160), rating: clamp(raw.armor?.rating, 0, 5), bonus: clamp(raw.armor?.bonus, 0, 3) },
    mentalProtection: clamp(raw.mentalProtection, 0, 20),
    gear: (Array.isArray(raw.gear) ? raw.gear : []).map((g) => cleanText(g).slice(0, 200)).filter(Boolean),
    keywords: fitArray(raw.keywords, 5), dispositions: fitArray(raw.dispositions, 4), cues: fitArray(raw.cues, 4),
    bio: cleanText(raw.bio),
    career: normalizeCareer(raw.career, makeId),
  };
};

// ---- Storage helpers ------------------------------------------------------

const store = {
  indexKey: "anarchy:v2:index",
  buildKey(id) { return "anarchy:v2:build:" + id; },
  async listIds() {
    const r = await storageProvider.get(this.indexKey);
    if (!r) return [];
    const parsed = JSON.parse(r.value);
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : [];
  },
  async saveIndex(ids) { await storageProvider.set(this.indexKey, JSON.stringify([...new Set(ids)])); },
  async get(id) {
    const r = await storageProvider.get(this.buildKey(id));
    return r ? normalizeBuild(JSON.parse(r.value)) : null;
  },
  async put(b) {
    const normalized = normalizeBuild(b);
    await storageProvider.set(this.buildKey(normalized.id), JSON.stringify(normalized));
    return normalized;
  },
  async del(id) { await storageProvider.delete(this.buildKey(id)); },
  async migrateLegacy() {
    const existing = await this.listIds();
    if (existing.length) return existing;
    const oldIndex = await storageProvider.get("anarchy:index");
    if (!oldIndex) return [];
    const names = JSON.parse(oldIndex.value);
    const migrated = [];
    for (const name of Array.isArray(names) ? names : []) {
      const old = await storageProvider.get("anarchy:build:" + name);
      if (!old) continue;
      const b = normalizeBuild(JSON.parse(old.value));
      await this.put(b); migrated.push(b.id);
    }
    await this.saveIndex(migrated);
    return migrated;
  },
};

// ---- Small UI atoms -------------------------------------------------------

const Btn = ({ children, onClick, tone = "ghost", title, style, disabled = false }) => (
  <button type="button" className={"ab-btn ab-" + tone} onClick={onClick} title={title} aria-label={title} style={style} disabled={disabled}>{children}</button>
);

const Field = ({ label, children }) => (
  <label className="ab-field"><span>{label}</span>{children}</label>
);

const ampBaseRating = (amp, amps) => {
  if (amp.type !== "Quality") return AMP_TYPES[amp.type]?.base || 0;
  return amps.filter((a) => a.type === "Quality").findIndex((a) => a.id === amp.id) <= 0 ? 0 : 3;
};
const ampMinimumRating = (amp, amps) => ampBaseRating(amp, amps) + (amp.rrEffects || []).reduce((sum, rr) => sum + RR_SCOPE_COST[rr.scope] * rr.level, 0);
const safeFilename = (name, ext) => ((name || "runner").replace(/[^\w -]/g, "").trim().replace(/\s+/g, "_") || "runner") + ext;
const mdCell = (value) => String(value ?? "").replace(/\r?\n/g, " ").replace(/\|/g, "\\|");
const PRINT_OPTIONS = [
  { id: "all", label: "Complete character", detail: "Core, detail, then backstory and notes" },
  { id: "gameplay", label: "Gameplay sheets", detail: "Core plus magic, amps, and gear" },
  { id: "core", label: "Core runner only", detail: "Attributes, Skills, traits, and Risk Dice" },
  { id: "detail", label: "Magic, amps, and gear only", detail: "The frequently updated equipment page" },
  { id: "story", label: "Backstory and notes only", detail: "Full-page narrative and writing space" },
];

// ---- Main -----------------------------------------------------------------

function AnarchyBuilder() {
  const [build, setBuild] = useState(() => blankBuild());
  const [library, setLibrary] = useState([]);
  const [tab, setTab] = useState("Attributes");
  const [ready, setReady] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState("library");
  const [restorePreview, setRestorePreview] = useState(null);
  const [printOpen, setPrintOpen] = useState(false);
  const [printScope, setPrintScope] = useState("all");
  const [helpOpen, setHelpOpen] = useState(false);
  const [driveOpen, setDriveOpen] = useState(false);
  const [driveConnected, setDriveConnected] = useState(false);
  const [driveBusy, setDriveBusy] = useState(false);
  const [driveBackups, setDriveBackups] = useState([]);
  const [driveFolderUrl, setDriveFolderUrl] = useState("");
  const [autoBackupEnabled, setAutoBackupEnabled] = useState(true);
  const [autoBackupBusy, setAutoBackupBusy] = useState(false);
  const [autoBackupError, setAutoBackupError] = useState("");
  const [lastAutoBackupAt, setLastAutoBackupAt] = useState("");
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState("");
  const [savedSnapshot, setSavedSnapshot] = useState("");
  const fileRef = useRef(null);
  const driveClientRef = useRef(null);
  const autoBackupTimerRef = useRef(null);
  const autoBackupInFlightRef = useRef(false);
  const driveAvailable = googleDriveEnvironmentAvailable(GOOGLE_CLIENT_ID);

  const announce = useCallback((message) => { setNotice(message); window.clearTimeout(announce.timer); announce.timer = window.setTimeout(() => setNotice(""), 2600); }, []);

  // Load existing v2 builds or migrate the original name-keyed format.
  useEffect(() => {
    (async () => {
      try {
        const ids = await store.migrateLegacy();
        const builds = (await Promise.all(ids.map((id) => store.get(id)))).filter(Boolean);
        setLibrary(builds.map((b) => ({ id: b.id, name: b.name })));
        if (builds[0]) { setBuild(builds[0]); setSavedSnapshot(JSON.stringify(builds[0])); }
        else { const b = blankBuild(); setBuild(b); setSavedSnapshot(JSON.stringify(b)); }
      } catch (err) {
        announce("Saved builds could not be loaded. Browser storage may be unavailable.");
      } finally { setReady(true); }
    })();
  }, [announce]);

  const meta = METATYPES[build.metatype];
  const level = LEVELS[build.level];
  const career = useMemo(() => careerTotals(build.career), [build.career]);
  const careerActive = useMemo(() => hasCareerActivity(build.career), [build.career]);

  const patch = useCallback((p) => setBuild((b) => ({ ...b, ...p })), []);
  const setAttr = (k, v) => setBuild((b) => ({ ...b, attributes: { ...b.attributes, [k]: v } }));

  // Metatype changes can lower an Attribute cap. Edge is always derived.
  useEffect(() => {
    setBuild((b) => {
      const m = METATYPES[b.metatype];
      const attributes = { ...b.attributes, EDGE: m.EDGE };
      BUY_ATTRS.forEach((k) => { attributes[k] = clamp(attributes[k], 1, m[k]); });
      return ATTRS.every((k) => b.attributes[k] === attributes[k]) ? b : { ...b, attributes };
    });
  }, [build.metatype]);

  const dirty = ready && JSON.stringify(build) !== savedSnapshot;
  useEffect(() => {
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  useEffect(() => {
    if (!exportOpen && !printOpen && !restorePreview && !helpOpen && !driveOpen) return;
    const close = (e) => { if (e.key === "Escape") { setExportOpen(false); setPrintOpen(false); setRestorePreview(null); setHelpOpen(false); setDriveOpen(false); } };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [exportOpen, printOpen, restorePreview, helpOpen, driveOpen]);

  const startPrint = () => {
    setPrintOpen(false);
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => window.print()));
  };

  // ----- cost breakdown -----
  const cost = useMemo(() => {
    const attrsC = BUY_ATTRS.reduce((s, k) => s + attrCost(build.attributes[k], meta[k]), 0);
    const skillsC = build.skills.reduce((s, sk) => s + skillCost(sk.rating) + 2500 * (sk.specs?.length || 0), 0) + build.knowledgeSkills.length * 2500;
    const spellsC = build.spells.length * 5000;
    const adeptC = build.adeptPowers.reduce((s, p) => s + p.rating * 5000, 0);
    const typeC = (AWAKENED_TYPES[build.awakenedType]?.rating || 0) * 5000;
    const ampsC = build.amps.reduce((s, a) => s + (a.rating || 0) * 5000 + (a.baseCost || 0), 0);
    const weaponsC = build.weapons.reduce((s, w) => s + (w.expert ? 5000 : 2500), 0);
    const armorC = (build.armor?.rating || 0) * 2500;
    const gearC = build.gear.length * 2500;
    const total = attrsC + skillsC + spellsC + adeptC + typeC + ampsC + weaponsC + armorC + gearC;
    return { attrsC, skillsC, spellsC, adeptC, typeC, ampsC, weaponsC, armorC, gearC,
      magicC: spellsC + adeptC + typeC, gearAllC: weaponsC + armorC + gearC + ampsC, total };
  }, [build, meta]);

  const remaining = level.budget - cost.total;

  // ----- derived warnings -----
  const warnings = useMemo(() => {
    const w = [];
    const awakened = AWAKENED_TYPES[build.awakenedType];
    if (!careerActive && remaining < 0) w.push(`Over budget by ${yen(-remaining)} — trim, or ask your GM for a higher level of play.`);
    const atMax = BUY_ATTRS.filter((k) => build.attributes[k] >= meta[k]).length;
    if (!careerActive && atMax > level.attrsAtMax) w.push(`${atMax} attributes at metatype max — ${build.level} allows ${level.attrsAtMax}.`);
    build.skills.forEach((sk) => {
      if (!careerActive && sk.rating > level.skillMax) w.push(`${sk.name} is rating ${sk.rating} — ${build.level} caps skills at ${level.skillMax}.`);
      if (sk.rating < 1 && sk.specs.length) w.push(`${sk.name} has specializations but is rating 0; specializations require rating 1.`);
    });
    if (build.skills.some((s) => s.name === "Sorcery") && !awakened.sorcery) w.push(`${build.awakenedType} does not include the Sorcery faculty.`);
    if (build.skills.some((s) => s.name === "Conjuring") && !awakened.conjuring) w.push(`${build.awakenedType} does not include the Conjuring faculty.`);
    if (build.spells.length && !awakened.sorcery) w.push("Spells require an Awakened type with the Sorcery faculty.");
    if (build.adeptPowers.length && !awakened.adept)
      w.push("Adept powers require the Adept or Mystic Adept type.");
    if (build.skills.some((s) => s.name === "Cracking") && !build.amps.some((a) => a.type === "Cyberdeck"))
      w.push("Cracking requires a Cyberdeck Amp.");
    const duplicateSpells = build.spells.map((s) => s.name.toLowerCase()).filter((n, i, all) => all.indexOf(n) !== i);
    if (duplicateSpells.length) w.push("The same spell has been added more than once.");
    const duplicatePowers = build.adeptPowers.map((p) => p.name).filter((n, i, all) => all.indexOf(n) !== i);
    if (duplicatePowers.length) w.push("The same adept power has been added more than once.");
    build.amps.forEach((amp) => {
      const min = ampMinimumRating(amp, build.amps);
      if (amp.rating < min) w.push(`${amp.name} needs at least rating ${min} for its type and structured RR effects.`);
      (amp.rrEffects || []).forEach((rr) => {
        const cap = level.rrMax[rr.scope];
        if (!careerActive && rr.level > cap) w.push(`${amp.name}: RR ${rr.level} on ${rr.target} exceeds the ${build.level} creation cap of ${cap}.`);
      });
    });
    const rrTotals = {};
    build.amps.forEach((amp) => (amp.rrEffects || []).forEach((rr) => {
      const key = `${rr.scope}:${rr.target.trim().toLowerCase()}`;
      rrTotals[key] = { scope: rr.scope, target: rr.target, level: (rrTotals[key]?.level || 0) + rr.level, count: (rrTotals[key]?.count || 0) + 1 };
    }));
    Object.values(rrTotals).forEach((rr) => {
      const cap = level.rrMax[rr.scope];
      if (!careerActive && rr.count > 1 && rr.level > cap) w.push(`Combined RR ${rr.level} on ${rr.target} exceeds the ${build.level} creation cap of ${cap}.`);
    });
    const essenceNow = 6 - build.amps.reduce((sum, a) => sum + (a.essence || 0), 0);
    if (essenceNow <= 0) w.push("Essence 0 means the character dies. Remove at least one implant.");
    else {
      if (essenceNow <= 5 && awakened.rating > 0) w.push("Essence 5 or lower cancels 1 hit on every magic-related Test.");
      if (essenceNow <= 4) w.push("Essence 4 or lower cancels 1 hit on Tests made to heal this character.");
      if (essenceNow <= 3 && awakened.rating > 0) w.push("Essence 3 or lower cancels a second hit on every magic-related Test.");
      if (essenceNow <= 2) w.push("Essence 2 or lower cancels a second hit on Tests made to heal this character.");
      if (essenceNow <= 1) w.push("Essence 1 or lower causes Disadvantage on most social Tests at the GM's discretion.");
    }
    if (build.keywords.some((x) => !x.trim())) w.push("Character creation calls for 5 Keywords.");
    if (build.dispositions.some((x) => !x.trim())) w.push("Character creation calls for 4 Dispositions.");
    if (build.cues.some((x) => !x.trim())) w.push("Character creation calls for 4 Cues.");
    return w;
  }, [build, meta, level, remaining, careerActive]);

  // ----- wound thresholds -----
  const armorR = (build.armor?.rating || 0) + (build.armor?.bonus || 0);
  const phys = [build.attributes.STR + armorR, build.attributes.STR + armorR + 3, build.attributes.STR + armorR + 6];
  const mentalR = build.mentalProtection || 0;
  const ment = [build.attributes.WIL + mentalR, build.attributes.WIL + mentalR + 3, build.attributes.WIL + mentalR + 6];
  const essence = Math.max(0, 6 - build.amps.reduce((sum, a) => sum + (a.essence || 0), 0));

  // ----- library ops -----
  const doSave = async () => {
    try {
      const saved = await store.put({ ...build, name: build.name.trim() || "Untitled Runner" });
      const next = library.some((x) => x.id === saved.id)
        ? library.map((x) => x.id === saved.id ? { id: saved.id, name: saved.name } : x)
        : [...library, { id: saved.id, name: saved.name }];
      await store.saveIndex(next.map((x) => x.id));
      setBuild(saved); setLibrary(next); setSavedSnapshot(JSON.stringify(saved)); announce("Build saved in this browser.");
    } catch (err) { announce("Save failed. Export a build file before closing this page."); }
  };
  const confirmDiscard = () => !dirty || window.confirm("Discard unsaved changes to this build?");
  const doLoad = async (id) => {
    if (!confirmDiscard()) return;
    try { const b = await store.get(id); if (b) { setBuild(b); setSavedSnapshot(JSON.stringify(b)); setTab("Attributes"); } }
    catch (err) { announce("That saved build could not be loaded."); }
  };
  const doNew = () => { if (confirmDiscard()) { const b = blankBuild(); setBuild(b); setSavedSnapshot(JSON.stringify(b)); setTab("Attributes"); } };
  const doDuplicate = () => {
    const b = normalizeBuild({ ...build, id: makeId(), name: `${build.name} (copy)` });
    setBuild(b); setSavedSnapshot(""); announce("Duplicate created; save it when ready.");
  };
  const doDelete = async () => {
    if (!library.some((x) => x.id === build.id)) { announce("This build has not been saved yet."); return; }
    if (!window.confirm(`Delete ${build.name || "this build"}? This cannot be undone.`)) return;
    try {
      await store.del(build.id);
      const next = library.filter((x) => x.id !== build.id);
      await store.saveIndex(next.map((x) => x.id)); setLibrary(next);
      if (next.length) { const b = await store.get(next[0].id); setBuild(b); setSavedSnapshot(JSON.stringify(b)); }
      else { const b = blankBuild(); setBuild(b); setSavedSnapshot(JSON.stringify(b)); }
      announce("Saved build deleted.");
    } catch (err) { announce("Delete failed; the saved build was not removed."); }
  };

  // ----- markdown export -----
  const markdown = useMemo(() => {
    const b = build, L = [];
    const title = b.streetname ? `${b.name} — "${b.streetname}"` : (b.name || "Untitled runner");
    L.push(`# ${title}`, "");
    L.push(`*${b.metatype} · ${b.awakenedType} · ${b.level}*`);
    if (b.bio) L.push("", b.bio);
    const section = (h, items) => { if (items.length) { L.push("", `## ${h}`, ""); items.forEach((x) => L.push(x)); } };
    section("Keywords", b.keywords.filter(Boolean).map((k) => `- ${k}`));
    section("Dispositions", b.dispositions.filter(Boolean).map((k) => `- ${k}`));
    section("Cues", b.cues.filter(Boolean).map((k) => `- *"${k}"*`));
    L.push("", "## Attributes", "", "| STR | AGI | WIL | LOG | CHA | EDGE |", "|:---:|:---:|:---:|:---:|:---:|:----:|",
      `| ${ATTRS.map((a) => b.attributes[a]).join(" | ")} |`, "", `**Essence ${essence.toFixed(1)} / 6.0**`);
    if (b.skills.length) {
      L.push("", "## Skills", "", "| Skill | Rating | Specializations |", "|---|:---:|---|");
      b.skills.forEach((s) => L.push(`| ${mdCell(s.name)} | ${s.rating} | ${mdCell(s.specs.join(", ") || "—")} |`));
    }
    section("Knowledge Skills", b.knowledgeSkills.map((s) => `- ${s}`));
    section("Spells", b.spells.map((s) => `- **${s.name}** *(${s.category})*`));
    section("Adept powers", b.adeptPowers.map((p) => `- **${p.name}** — ${ADEPT_POWERS[p.name]?.effect || ""} *(R${p.rating})*`));
    section("Shadow Amps", b.amps.map((a) => {
      const rr = (a.rrEffects || []).map((r) => `RR ${r.level} on ${r.scope} ${r.target}`).join("; ");
      const details = [a.effect, rr].filter(Boolean).join(" — ");
      return `- **${a.name}** *(${a.type}, R${a.rating}${a.essence ? `, −${a.essence} Essence` : ""})*${details ? ` — ${details}` : ""}`;
    }));
    if (b.weapons.length) {
      L.push("", "## Weapons", "", "| Weapon | DV | Range C/S/M/L |", "|---|:---:|:---:|");
      b.weapons.forEach((w) => L.push(`| ${mdCell(w.name)} | ${mdCell(w.dv)}${w.expert ? " (expert)" : ""} | ${mdCell(w.range)} |`));
    }
    const gearLines = [];
    if (b.armor?.name) gearLines.push(`- **${b.armor.name}** (Armor ${b.armor.rating}${b.armor.bonus ? ` + ${b.armor.bonus} stacking bonus` : ""})`);
    if (b.mentalProtection) gearLines.push(`- Mental Protection ${b.mentalProtection}`);
    gearLines.push("- Commlink + trodes", "- Fake SIN", ...b.gear.map((g) => `- ${g}`));
    section("Gear", gearLines);
    L.push("", "## Condition monitor", "", "| | Light | Serious | Incap |", "|---|:---:|:---:|:---:|",
      `| Physical | ${phys[0]} | ${phys[1]} | ${phys[2]} |`, `| Mental | ${ment[0]} | ${ment[1]} | ${ment[2]} |`);
    if (careerActive) {
      L.push("", "## Career & Nuyen", "", `**${career.jobsCompleted} completed job${career.jobsCompleted === 1 ? "" : "s"} · ${yen(career.balance)} available · ${yen(career.netEarned)} net earned · ${yen(career.advancementSpent)} invested**`);
      if (b.career.jobs.length) {
        L.push("", "### Job Ledger", "", "| Completed | Job | Client | Payout | Expenses | Net |", "|---|---|---|---:|---:|---:|");
        b.career.jobs.forEach((job) => L.push(`| ${job.completedOn || "—"} | ${mdCell(job.name)} | ${mdCell(job.client || "—")} | ${yen(job.payout)} | ${yen(job.expenses)} | ${yen(job.payout - job.expenses)} |`));
      }
      if (b.career.advancements.length) section("Advancement Ledger", b.career.advancements.map((item) => `- **${item.label}** — ${yen(item.cost)}${item.purchasedOn ? ` · ${item.purchasedOn}` : ""}${item.details ? ` — ${item.details}` : ""}`));
    }
    L.push("", "---", `*Character value: ${yen(cost.total)} · Starting tier: ${b.level} (${yen(level.budget)})${careerActive ? ` · Career balance: ${yen(career.balance)}` : ` · ${remaining < 0 ? yen(-remaining) + " over" : yen(remaining) + " remaining"}`}*`, "",
      "*Unofficial fan utility output. Shadowrun and associated marks are trademarks of The Topps Company, Inc. This utility is not affiliated with or endorsed by The Topps Company, Inc. or Catalyst Game Labs.*");
    return L.join("\n");
  }, [build, cost, level, remaining, phys, ment, essence, career, careerActive]);

  const copyMd = async () => {
    try { await navigator.clipboard.writeText(markdown); setCopied(true); setTimeout(() => setCopied(false), 1600); }
    catch { announce("Clipboard access failed; select the text and copy it manually."); }
  };
  const downloadMd = () => {
    try {
      const blob = new Blob([markdown], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = safeFilename(build.name, ".md");
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { announce("Markdown download failed."); }
  };

  // ----- character and full-library import / export -----
  const downloadJson = (value, filename) => {
    const blob = new Blob([JSON.stringify(value, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const exportCharacterJson = () => {
    try {
      downloadJson(build, safeFilename(build.name, ".json"));
      announce("Character backup downloaded.");
    } catch (err) { announce("Character backup download failed."); }
  };
  const createLibraryBackupSnapshot = async () => {
    const ids = await store.listIds();
    const saved = (await Promise.all(ids.map((id) => store.get(id)))).filter(Boolean);
    const snapshots = new Map(saved.map((item) => [item.id, item]));
    if (dirty || library.some((item) => item.id === build.id)) snapshots.set(build.id, normalizeBuild(build));
    const characters = [...snapshots.values()];
    if (!characters.length) throw new Error("There are no saved or edited runners to back up yet.");
    const createdAt = new Date().toISOString();
    return createLibraryBackup({ appVersion: APP_VERSION, characters, createdAt });
  };
  const openLibraryBackup = (value, fileName) => {
    const backup = parseLibraryBackup(value);
    if (!backup) return false;
    const entries = backup.characters.map((candidate, index) => {
      try {
        const normalized = normalizeBuild(candidate);
        return { index, build: normalized, name: normalized.name, selected: true, error: "" };
      } catch (err) {
        return { index, build: null, name: cleanText(candidate?.name, `Character ${index + 1}`).slice(0, 160), selected: false, error: err.message || "Invalid character data" };
      }
    });
    if (!entries.some((entry) => entry.build)) throw new Error("That library backup contains no restorable characters.");
    setRestorePreview({ fileName, createdAt: backup.createdAt, appVersion: backup.appVersion, entries });
    announce(`Library backup opened with ${entries.filter((entry) => entry.build).length} restorable runner${entries.filter((entry) => entry.build).length === 1 ? "" : "s"}.`);
    return true;
  };
  const exportLibraryJson = async () => {
    try {
      const backup = await createLibraryBackupSnapshot();
      downloadJson(backup, libraryBackupFilename(backup.createdAt));
      announce(`Library backup downloaded with ${backup.characterCount} runner${backup.characterCount === 1 ? "" : "s"}.`);
    } catch (err) { announce(err.message || "Library backup download failed."); }
  };
  const importJson = (e) => {
    const f = e.target.files && e.target.files[0]; if (!f) return;
    if (f.size > MAX_IMPORT_BYTES) { announce("Import files must be smaller than 10 MB."); e.target.value = ""; return; }
    const r = new FileReader();
    r.onload = () => {
      try {
        const parsed = JSON.parse(r.result);
        if (openLibraryBackup(parsed, f.name)) return;
        if (!confirmDiscard()) return;
        const b = normalizeBuild(parsed, { newIdentity: true });
        setBuild(b); setSavedSnapshot(""); setTab("Attributes"); announce("Build imported as a new unsaved character.");
      } catch (err) { announce(err.message || "That build file is invalid."); }
    };
    r.onerror = () => announce("That build file could not be read.");
    r.readAsText(f); e.target.value = "";
  };
  const setAllRestoreEntries = (selected) => setRestorePreview((preview) => preview ? ({
    ...preview,
    entries: preview.entries.map((entry) => ({ ...entry, selected: Boolean(entry.build) && selected })),
  }) : preview);
  const toggleRestoreEntry = (index) => setRestorePreview((preview) => preview ? ({
    ...preview,
    entries: preview.entries.map((entry) => entry.index === index && entry.build ? { ...entry, selected: !entry.selected } : entry),
  }) : preview);
  const restoreLibraryBackup = async () => {
    if (!restorePreview) return;
    const selected = restorePreview.entries.filter((entry) => entry.build && entry.selected);
    if (!selected.length) { announce("Choose at least one runner to restore."); return; }
    const writtenIds = [];
    try {
      const usedNames = new Set(library.map((item) => item.name.trim().toLowerCase()));
      const restored = selected.map((entry) => {
        const originalName = entry.build.name.trim() || "Untitled Runner";
        let name = originalName;
        if (usedNames.has(name.toLowerCase())) {
          name = `${originalName} (restored)`;
          let suffix = 2;
          while (usedNames.has(name.toLowerCase())) name = `${originalName} (restored ${suffix++})`;
        }
        usedNames.add(name.toLowerCase());
        return normalizeBuild({ ...entry.build, name }, { newIdentity: true });
      });
      for (const restoredBuild of restored) {
        const saved = await store.put(restoredBuild);
        writtenIds.push(saved.id);
      }
      const existingIds = await store.listIds();
      await store.saveIndex([...existingIds, ...writtenIds]);
      setLibrary((current) => [...current, ...restored.map((item) => ({ id: item.id, name: item.name }))]);
      setRestorePreview(null);
      announce(`Restored ${restored.length} runner${restored.length === 1 ? "" : "s"} as new browser saves.`);
    } catch (err) {
      await Promise.all(writtenIds.map((id) => store.del(id).catch(() => {})));
      announce(err.message || "The library backup could not be restored.");
    }
  };

  // ----- user-owned Google Drive backups -----
  const getDriveClient = () => {
    if (!driveClientRef.current) driveClientRef.current = new GoogleDriveBackupClient({ clientId: GOOGLE_CLIENT_ID });
    return driveClientRef.current;
  };
  const refreshDriveBackups = async (client = getDriveClient()) => {
    const files = await client.listLibraryBackups();
    setDriveBackups(files);
    setDriveFolderUrl(client.folderUrl());
    return files;
  };
  const connectGoogleDrive = async () => {
    if (!driveAvailable) { announce(GOOGLE_CLIENT_ID ? "Google Drive requires the hosted HTTPS version." : "Google Drive has not been configured by the site owner yet."); return; }
    setDriveBusy(true);
    try {
      const client = getDriveClient();
      await client.connect();
      await refreshDriveBackups(client);
      setDriveConnected(true);
      setAutoBackupError("");
      announce("Google Drive connected. Automatic backup is on for this session.");
    } catch (err) {
      setDriveConnected(false);
      announce(err.message || "Google Drive could not be connected.");
    } finally { setDriveBusy(false); }
  };
  const backUpToGoogleDrive = async () => {
    setDriveBusy(true);
    try {
      const client = getDriveClient();
      if (!client.connected) throw new Error("Connect Google Drive before creating a cloud backup.");
      const backup = await createLibraryBackupSnapshot();
      const filename = libraryBackupFilename(backup.createdAt);
      await client.uploadLibraryBackup({ backup, filename, appVersion: APP_VERSION });
      await refreshDriveBackups(client);
      announce(`Google Drive backup created with ${backup.characterCount} runner${backup.characterCount === 1 ? "" : "s"}.`);
    } catch (err) {
      if (!getDriveClient().connected) setDriveConnected(false);
      announce(err.message || "Google Drive backup failed.");
    } finally { setDriveBusy(false); }
  };
  const automaticallyBackUpToGoogleDrive = async () => {
    if (!driveConnected || !autoBackupEnabled || driveBusy || autoBackupInFlightRef.current) return;
    autoBackupInFlightRef.current = true;
    setAutoBackupBusy(true);
    setAutoBackupError("");
    try {
      const client = getDriveClient();
      if (!client.connected) throw new Error("Google Drive authorization has expired. Connect again to resume automatic backup.");
      const backup = await createLibraryBackupSnapshot();
      await client.upsertAutomaticBackup({ backup, appVersion: APP_VERSION });
      await refreshDriveBackups(client);
      setLastAutoBackupAt(new Date().toISOString());
    } catch (err) {
      if (String(err.message || "").includes("There are no saved or edited runners")) return;
      if (!getDriveClient().connected) setDriveConnected(false);
      setAutoBackupError(err.message || "Automatic Google Drive backup failed.");
    } finally {
      autoBackupInFlightRef.current = false;
      setAutoBackupBusy(false);
    }
  };
  const restoreFromGoogleDrive = async (file) => {
    if (Number(file.size) > MAX_IMPORT_BYTES) { announce("Google Drive backups must be smaller than 10 MB."); return; }
    setDriveBusy(true);
    try {
      const client = getDriveClient();
      const parsed = await client.downloadLibraryBackup(file.id);
      if (!openLibraryBackup(parsed, file.name)) throw new Error("That Drive file is not a Runner Forge library backup.");
      setDriveOpen(false);
    } catch (err) {
      if (!getDriveClient().connected) setDriveConnected(false);
      announce(err.message || "Google Drive backup could not be opened.");
    } finally { setDriveBusy(false); }
  };
  const disconnectGoogleDrive = async () => {
    setDriveBusy(true);
    try { await getDriveClient().disconnect(); }
    finally {
      setDriveConnected(false);
      setDriveBackups([]);
      setDriveFolderUrl("");
      setAutoBackupError("");
      setLastAutoBackupAt("");
      setDriveBusy(false);
      announce("Google Drive disconnected from Runner Forge.");
    }
  };
  const openGoogleDriveFolder = () => {
    if (driveFolderUrl) window.open(driveFolderUrl, "_blank", "noopener,noreferrer");
  };

  const autoBackupRevision = useMemo(() => JSON.stringify({ build, library }), [build, library]);
  useEffect(() => {
    window.clearTimeout(autoBackupTimerRef.current);
    if (!ready || !driveConnected || !autoBackupEnabled || driveBusy) return undefined;
    autoBackupTimerRef.current = window.setTimeout(automaticallyBackUpToGoogleDrive, AUTO_BACKUP_DELAY_MS);
    return () => window.clearTimeout(autoBackupTimerRef.current);
  }, [ready, driveConnected, autoBackupEnabled, driveBusy, autoBackupRevision]);

  return (
    <div className="ab-root">
      <style>{CSS}</style>
      <div className="ab-screen">

      {/* ---- Masthead ---- */}
      <header className="ab-mast">
        <div className="ab-mast-title">
          <span className="ab-brand">FOR SHADOWRUN: ANARCHY 2.0</span>
          <span className="ab-brand-2">RUNNER FORGE</span>
          <span className="ab-brand-tag">unofficial fan utility</span>
        </div>
        <div className="ab-lib">
          <div className="ab-select-wrap">
            <select aria-label="Load a saved build" value="" disabled={!ready || !library.length} onChange={(e) => e.target.value && doLoad(e.target.value)}>
              <option value="">{library.length ? "load build…" : "no saved builds"}</option>
              {library.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
            <ChevronDown size={14} />
          </div>
          <Btn tone="gold" onClick={doSave} title="Save build in this browser"><Save size={14} /> save{dirty ? "*" : ""}</Btn>
          <Btn onClick={doNew} title="Start blank"><FilePlus size={14} /> new</Btn>
          <Btn onClick={doDuplicate} title="Duplicate this build"><Copy size={14} /></Btn>
          <Btn onClick={() => { setExportFormat("library"); setExportOpen(true); }} title="Export character, library backup, or Markdown sheet"><Download size={14} /> export</Btn>
          <Btn onClick={() => setPrintOpen(true)} title="Choose and print character sheets"><Printer size={14} /> print</Btn>
          <Btn onClick={() => fileRef.current && fileRef.current.click()} title="Import a character or library backup"><FilePlus size={14} /> import</Btn>
          <input ref={fileRef} type="file" accept="application/json,.json" style={{ display: "none" }} onChange={importJson} />
          <Btn onClick={() => setDriveOpen(true)} title="Back up or restore the library with Google Drive"><Cloud size={14} /> drive</Btn>
          <Btn onClick={() => setHelpOpen(true)} title="Help, privacy, and security information"><HelpCircle size={14} /> help</Btn>
          <Btn tone="danger" onClick={doDelete} title="Delete saved build"><Trash2 size={14} /></Btn>
        </div>
      </header>
      <div className="ab-notice" aria-live="polite">{notice}</div>

      <div className="ab-grid">
        {/* ================= MAIN COLUMN ================= */}
        <main className="ab-main">
          {/* identity */}
          <section className="ab-panel ab-identity">
            <div className="ab-id-row">
              <Field label="Real name"><input value={build.name} onChange={(e) => patch({ name: e.target.value })} /></Field>
              <Field label="Street handle"><input value={build.streetname} onChange={(e) => patch({ streetname: e.target.value })} placeholder="e.g. Púca" /></Field>
            </div>
            <div className="ab-id-row">
              <Field label="Metatype">
                <select value={build.metatype} onChange={(e) => patch({ metatype: e.target.value })}>
                  {Object.keys(METATYPES).map((m) => <option key={m}>{m}</option>)}
                </select>
              </Field>
              <Field label={careerActive ? "Starting level of play" : "Level of play"}>
                <select value={build.level} onChange={(e) => patch({ level: e.target.value })}>
                  {Object.keys(LEVELS).map((l) => <option key={l}>{l}</option>)}
                </select>
              </Field>
              <Field label="Awakened type">
                <select value={build.awakenedType} onChange={(e) => patch({ awakenedType: e.target.value })}>
                  {Object.keys(AWAKENED_TYPES).map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
            </div>
            <p className="ab-meta-note">{meta.quality} · {AWAKENED_TYPES[build.awakenedType].faculties}</p>
          </section>

          <section className={"ab-mobile-budget" + ((!careerActive && remaining < 0) || career.balance < 0 ? " over" : "")} aria-label="Build status">
            <b>{careerActive ? `${yen(career.balance)} available` : `${yen(cost.total)} / ${yen(level.budget)}`}</b>
            <span>{careerActive ? `${career.jobsCompleted} jobs · ${yen(career.advancementSpent)} invested` : (remaining < 0 ? `${yen(-remaining)} over` : `${yen(remaining)} left`)} · {warnings.length} check{warnings.length === 1 ? "" : "s"}</span>
          </section>

          {/* tabs */}
          <nav className="ab-tabs" role="tablist" aria-label="Character sections">
            {["Attributes", "Skills", "Magic", "Gear", "Career", "Story"].map((t) => (
              <button type="button" role="tab" aria-selected={tab === t} key={t} className={"ab-tab" + (tab === t ? " on" : "")} onClick={() => setTab(t)}>{t}</button>
            ))}
          </nav>

          {tab === "Attributes" && <AttrPanel build={build} meta={meta} setAttr={setAttr} />}
          {tab === "Skills" && <SkillPanel build={build} setBuild={setBuild} />}
          {tab === "Magic" && <MagicPanel build={build} setBuild={setBuild} />}
          {tab === "Gear" && <GearPanel build={build} setBuild={setBuild} patch={patch} essence={essence} />}
          {tab === "Career" && <CareerPanel build={build} setBuild={setBuild} meta={meta} career={career} announce={announce} />}
          {tab === "Story" && <StoryPanel build={build} setBuild={setBuild} patch={patch} />}
        </main>

        {/* ================= SIDEBAR ================= */}
        <aside className="ab-side">
          {/* budget meter — signature element */}
          <section className="ab-panel ab-budget">
            <div className="ab-budget-head">
              <span>{careerActive ? "CAREER NUYEN" : "BUILD NUYEN"}</span>
              <span className={careerActive ? (career.balance < 0 ? "ab-over" : "") : (remaining < 0 ? "ab-over" : "")}>{careerActive ? yen(career.balance) : `${yen(cost.total)} / ${yen(level.budget)}`}</span>
            </div>
            {careerActive ? <>
              <div className={"ab-career-balance" + (career.balance < 0 ? " over" : "")}><b>{yen(career.balance)}</b><span>available to advance</span></div>
              <div className="ab-cost-lines">
                <CostLine k="Completed jobs" v={career.jobsCompleted} money={false} />
                <CostLine k="Gross payouts" v={career.gross} />
                <CostLine k="Job expenses" v={-career.jobExpenses} />
                <CostLine k="Advancements" v={-career.advancementSpent} />
                {career.adjustments !== 0 && <CostLine k="Adjustments" v={career.adjustments} />}
              </div>
              <Btn onClick={() => setTab("Career")} title="Open career and advancement ledger" style={{ width: "100%", justifyContent: "center", marginTop: 10 }}>career ledger</Btn>
            </> : <>
              <div className="ab-meter">
                <div className={"ab-meter-fill" + (remaining < 0 ? " over" : "")}
                     style={{ width: Math.min(100, (cost.total / level.budget) * 100) + "%" }} />
              </div>
              <div className={"ab-remaining" + (remaining < 0 ? " over" : "")}>
                {remaining < 0 ? yen(-remaining) + " over" : yen(remaining) + " left"}
              </div>
              <div className="ab-cost-lines">
                <CostLine k="Attributes" v={cost.attrsC} />
                <CostLine k="Skills + specs" v={cost.skillsC} />
                <CostLine k="Magic (type/spells/powers)" v={cost.magicC} />
                <CostLine k="Gear · weapons · amps" v={cost.gearAllC} />
              </div>
            </>}
          </section>

          {warnings.length > 0 && (
            <section className="ab-panel ab-warn">
              <div className="ab-warn-head"><AlertTriangle size={14} /> check with your GM</div>
              <ul>{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </section>
          )}

          {/* condition monitor */}
          <section className="ab-panel">
            <h3 className="ab-h3"><Skull size={13} /> Condition monitor</h3>
            <table className="ab-wound">
              <thead><tr><th></th><th>Light</th><th>Serious</th><th>Incap</th></tr></thead>
              <tbody>
                <tr><td>Physical</td><td>{phys[0]}</td><td>{phys[1]}</td><td>{phys[2]}</td></tr>
                <tr><td>Mental</td><td>{ment[0]}</td><td>{ment[1]}</td><td>{ment[2]}</td></tr>
              </tbody>
            </table>
            <div className="ab-essence">ESSENCE <b>{essence.toFixed(1)}</b><span> / 6.0</span></div>
          </section>

          {/* risk table */}
          <section className="ab-panel">
            <h3 className="ab-h3"><Dice5 size={13} /> Risk dice</h3>
            <table className="ab-risk">
              <thead><tr><th>RR</th><th>Low</th><th>Norm</th><th>High</th><th>Extr</th></tr></thead>
              <tbody>
                {RISK_TABLE.map((row, i) => (
                  <tr key={i}><td className="ab-rr">{i}</td>{row.map((c, j) => <td key={j}>{c}</td>)}</tr>
                ))}
              </tbody>
            </table>
            <p className="ab-tiny">Hits on risk dice count double · a rolled 1 glitches · your RR ignores that many 1s.</p>
          </section>
        </aside>
      </div>

      {helpOpen && (
        <div className="ab-modal-bg" onClick={() => setHelpOpen(false)}>
          <div className="ab-modal ab-help-dialog" role="dialog" aria-modal="true" aria-labelledby="help-title" onClick={(e) => e.stopPropagation()}>
            <div className="ab-modal-head">
              <span id="help-title"><HelpCircle size={14} /> Runner Forge help</span>
              <button type="button" className="ab-x" aria-label="Close help dialog" onClick={() => setHelpOpen(false)}><X size={16} /></button>
            </div>
            <div className="ab-help-body">
              <section>
                <h3>Top-line controls</h3>
                <dl className="ab-help-controls">
                  <div><dt>Load build…</dt><dd>Opens a runner previously saved by this site in this browser.</dd></div>
                  <div><dt>Save</dt><dd>Writes the current runner to browser storage. An asterisk means it has unsaved changes.</dd></div>
                  <div><dt>New</dt><dd>Starts a blank runner after warning about unsaved changes.</dd></div>
                  <div><dt>Duplicate</dt><dd>Creates an unsaved copy with a new internal ID.</dd></div>
                  <div><dt>Export</dt><dd>Downloads the full library, one editable runner, or a readable Markdown sheet.</dd></div>
                  <div><dt>Print</dt><dd>Chooses which character-sheet pages to send to the browser print dialog.</dd></div>
                  <div><dt>Import</dt><dd>Opens a runner JSON file or previews a full-library backup before restoring it.</dd></div>
                  <div><dt>Drive</dt><dd>Connects the user's own Google Drive for rolling automatic backup, timestamped manual backups, and selective restore.</dd></div>
                  <div><dt>Help</dt><dd>Opens this explanation. A small miracle of recursive documentation.</dd></div>
                  <div><dt>Trash</dt><dd>Deletes only the currently loaded browser save after confirmation.</dd></div>
                </dl>
              </section>
              <section>
                <h3><Shield size={15} /> Security and privacy</h3>
                <ul className="ab-help-list">
                  <li>Browser saves stay inside this site's origin. Runner Forge has no account database and sends no character data to Soulpyre Studios.</li>
                  <li>There is no advertising, analytics, tracking pixel, or telemetry in the application.</li>
                  <li>Imported backups are size-limited, parsed as JSON, validated, and previewed before they change browser storage.</li>
                  <li>The hosted site uses a restrictive Content Security Policy, clickjacking protection, MIME-sniffing protection, and explicit browser permission restrictions.</li>
                  <li>Google Drive is optional. Its authorization library loads only after Connect is selected, and the access token remains in memory rather than browser storage.</li>
                  <li>Runner Forge requests <code>drive.file</code>, which limits it to Drive files used or created through this app. It does not request access to the rest of the user's Drive.</li>
                  <li>While Google Drive is connected, automatic backup is enabled for that page session and updates one clearly named rolling file after 15 seconds without changes. It can be paused in the Drive dialog.</li>
                  <li>Automatic backup stops when the page closes, reloads, disconnects, or the short-lived authorization expires. No Google token is stored for unattended access.</li>
                </ul>
                <p className="ab-help-note">Manual JSON export always remains available. Google access can be disconnected here or removed from the user's Google Account connections.</p>
              </section>
            </div>
            <div className="ab-modal-foot">
              <Btn tone="gold" onClick={() => setHelpOpen(false)} title="Close help">got it</Btn>
              <a className="ab-text-link" href="./privacy.html" target="_blank" rel="noreferrer">privacy policy <ExternalLink size={12} /></a>
              <a className="ab-text-link" href="https://myaccount.google.com/connections" target="_blank" rel="noreferrer">Google account connections <ExternalLink size={12} /></a>
              <span className="ab-tiny">Runner Forge v{APP_VERSION}</span>
            </div>
          </div>
        </div>
      )}

      {driveOpen && (
        <div className="ab-modal-bg" onClick={() => !driveBusy && setDriveOpen(false)}>
          <div className="ab-modal ab-drive-dialog" role="dialog" aria-modal="true" aria-labelledby="drive-title" onClick={(e) => e.stopPropagation()}>
            <div className="ab-modal-head">
              <span id="drive-title"><Cloud size={14} /> Google Drive backups</span>
              <button type="button" className="ab-x" aria-label="Close Google Drive dialog" disabled={driveBusy} onClick={() => setDriveOpen(false)}><X size={16} /></button>
            </div>
            <div className="ab-drive-body">
              {!GOOGLE_CLIENT_ID ? (
                <div className="ab-drive-status warn">
                  <b>Google Drive needs its public OAuth client ID.</b>
                  <span>The integration is installed, but this deployment has not yet been connected to its Google Cloud web application. Add the client ID to <code>runner-forge-config.js</code> or the build environment, then redeploy.</span>
                </div>
              ) : !driveAvailable ? (
                <div className="ab-drive-status warn">
                  <b>Open the hosted HTTPS version to use Google Drive.</b>
                  <span>Google does not authorize Drive access from a downloaded <code>file://</code> page. Local saves and manual JSON backups still work normally.</span>
                </div>
              ) : !driveConnected ? (
                <>
                  <div className="ab-drive-status">
                    <b>Your Drive, not ours.</b>
                    <span>Runner Forge requests only <code>{GOOGLE_DRIVE_SCOPE.split("/").pop()}</code> and creates a visible <b>{GOOGLE_DRIVE_FOLDER}</b> folder. The authorization token is kept only for this page session.</span>
                  </div>
                  <div className="ab-drive-connect"><Btn tone="gold" onClick={connectGoogleDrive} title="Authorize Runner Forge to use its Google Drive backup files" disabled={driveBusy}><Cloud size={14} /> {driveBusy ? "connecting…" : "connect Google Drive"}</Btn></div>
                </>
              ) : (
                <>
                  <div className="ab-drive-status connected"><Check size={16} /><span><b>Connected for this session</b>Runner Forge can update one rolling automatic backup while this page remains authorized.</span></div>
                  <label className="ab-drive-auto">
                    <input type="checkbox" checked={autoBackupEnabled} onChange={(event) => { setAutoBackupEnabled(event.target.checked); setAutoBackupError(""); }} disabled={autoBackupBusy || driveBusy} />
                    <span><b>Automatic Drive backup for this session</b><small>After 15 seconds without changes, update <em>{GOOGLE_DRIVE_AUTO_BACKUP}</em>. The access token stays in page memory.</small></span>
                  </label>
                  <p className={"ab-drive-auto-state" + (autoBackupError ? " error" : "")}>
                    {autoBackupBusy ? "Saving automatic backup…" : autoBackupError ? autoBackupError : lastAutoBackupAt ? `Automatic backup updated ${new Date(lastAutoBackupAt).toLocaleTimeString()}.` : autoBackupEnabled ? "Automatic backup is waiting for the next change." : "Automatic backup is paused for this session."}
                  </p>
                  <div className="ab-drive-actions">
                    <Btn tone="gold" onClick={backUpToGoogleDrive} title="Create a timestamped full-library backup in Google Drive" disabled={driveBusy || autoBackupBusy}><Cloud size={14} /> back up now</Btn>
                    <Btn onClick={() => { setDriveBusy(true); refreshDriveBackups().catch((err) => announce(err.message)).finally(() => setDriveBusy(false)); }} title="Refresh Google Drive backup list" disabled={driveBusy || autoBackupBusy}>refresh</Btn>
                    <Btn onClick={openGoogleDriveFolder} title="Open the Runner Forge Backups folder in Google Drive" disabled={!driveFolderUrl || driveBusy || autoBackupBusy}><ExternalLink size={14} /> open folder</Btn>
                  </div>
                  <div className="ab-drive-list" aria-label="Google Drive library backups">
                    {driveBackups.length ? driveBackups.map((file) => (
                      <div className="ab-drive-file" key={file.id}>
                        <span><b>{file.name}{file.appProperties?.runnerForgeBackupMode === "automatic" ? " · automatic" : ""}</b><small>{new Date(file.modifiedTime || file.createdTime).toLocaleString()} · {Math.max(1, Math.round(Number(file.size || 0) / 1024))} KB</small></span>
                        <Btn onClick={() => restoreFromGoogleDrive(file)} title={`Preview and restore ${file.name}`} disabled={driveBusy || autoBackupBusy}>restore</Btn>
                      </div>
                    )) : <p className="ab-empty">No Runner Forge backups are in this Drive folder yet.</p>}
                  </div>
                </>
              )}
            </div>
            <div className="ab-modal-foot">
              {driveConnected && <Btn tone="danger" onClick={disconnectGoogleDrive} title="Revoke this Runner Forge Google Drive session" disabled={driveBusy || autoBackupBusy}>disconnect</Btn>}
              <Btn onClick={() => setDriveOpen(false)} title="Close Google Drive backups" disabled={driveBusy}>close</Btn>
              <span className="ab-tiny">Manual browser export remains available whether Drive is connected or not.</span>
            </div>
          </div>
        </div>
      )}

      {exportOpen && (
        <div className="ab-modal-bg" onClick={() => setExportOpen(false)}>
          <div className="ab-modal" role="dialog" aria-modal="true" aria-labelledby="export-title" onClick={(e) => e.stopPropagation()}>
            <div className="ab-modal-head">
              <span id="export-title"><Download size={14} /> Runner Forge — export</span>
              <button type="button" className="ab-x" aria-label="Close export dialog" onClick={() => setExportOpen(false)}><X size={16} /></button>
            </div>
            <fieldset className="ab-print-options ab-export-options">
              <legend>Export format</legend>
              <label className={exportFormat === "library" ? "selected" : ""}>
                <input type="radio" name="export-format" value="library" checked={exportFormat === "library"} onChange={() => setExportFormat("library")} />
                <span><b>Full library backup (.json)</b><small>Every saved runner, plus current unsaved changes. Import later to preview and restore selected characters.</small></span>
              </label>
              <label className={exportFormat === "character" ? "selected" : ""}>
                <input type="radio" name="export-format" value="character" checked={exportFormat === "character"} onChange={() => setExportFormat("character")} />
                <span><b>Current runner backup (.json)</b><small>One complete editable character for transfer or safekeeping.</small></span>
              </label>
              <label className={exportFormat === "markdown" ? "selected" : ""}>
                <input type="radio" name="export-format" value="markdown" checked={exportFormat === "markdown"} onChange={() => setExportFormat("markdown")} />
                <span><b>Markdown character sheet (.md)</b><small>Readable campaign record for Obsidian or another text editor. This format cannot be imported.</small></span>
              </label>
            </fieldset>
            {exportFormat === "markdown" && (
              <textarea className="ab-modal-text" aria-label="Exported character sheet in Markdown" autoFocus readOnly value={markdown} onFocus={(e) => e.target.select()} />
            )}
            <div className="ab-modal-foot">
              {exportFormat === "library" ? (
                <Btn tone="gold" onClick={exportLibraryJson}><Download size={14} /> download library .json</Btn>
              ) : exportFormat === "character" ? (
                <Btn tone="gold" onClick={exportCharacterJson}><Download size={14} /> download runner .json</Btn>
              ) : (
                <>
                  <Btn tone="gold" onClick={downloadMd}><Download size={14} /> download .md</Btn>
                  <Btn onClick={copyMd}>{copied ? <><Check size={14} /> copied</> : <><Copy size={14} /> copy Markdown</>}</Btn>
                </>
              )}
              <span className="ab-tiny">{exportFormat === "library" ? "A library backup restores characters as new browser saves without overwriting the current library." : exportFormat === "character" ? "A runner backup opens as one new unsaved character." : "Markdown is for reading and sharing, not restoring a build."}</span>
            </div>
          </div>
        </div>
      )}

      {restorePreview && (
        <div className="ab-modal-bg" onClick={() => setRestorePreview(null)}>
          <div className="ab-modal ab-restore-dialog" role="dialog" aria-modal="true" aria-labelledby="restore-title" onClick={(e) => e.stopPropagation()}>
            <div className="ab-modal-head">
              <span id="restore-title"><FilePlus size={14} /> restore library backup</span>
              <button type="button" className="ab-x" aria-label="Close restore dialog" onClick={() => setRestorePreview(null)}><X size={16} /></button>
            </div>
            <div className="ab-restore-summary">
              <b>{restorePreview.fileName}</b>
              <span>Created {new Date(restorePreview.createdAt).toLocaleString()} with Runner Forge v{restorePreview.appVersion}.</span>
              <span>Selected runners will be added as new browser saves. Existing characters will not be overwritten.</span>
            </div>
            <div className="ab-restore-tools">
              <Btn onClick={() => setAllRestoreEntries(true)} title="Select all valid characters">select all</Btn>
              <Btn onClick={() => setAllRestoreEntries(false)} title="Clear character selection">select none</Btn>
            </div>
            <div className="ab-restore-list" role="group" aria-label="Characters available to restore">
              {restorePreview.entries.map((entry) => (
                <label key={entry.index} className={entry.selected ? "selected" : ""}>
                  <input type="checkbox" checked={entry.selected} disabled={!entry.build} onChange={() => toggleRestoreEntry(entry.index)} />
                  <span>
                    <b>{entry.name || `Character ${entry.index + 1}`}</b>
                    <small>{entry.build ? `${entry.build.metatype} · ${entry.build.awakenedType} · ${entry.build.level}` : entry.error}</small>
                  </span>
                </label>
              ))}
            </div>
            <div className="ab-modal-foot">
              <Btn tone="gold" onClick={restoreLibraryBackup} title="Restore selected characters"><FilePlus size={14} /> restore selected</Btn>
              <Btn onClick={() => setRestorePreview(null)} title="Cancel library restore">cancel</Btn>
              <span className="ab-tiny">{restorePreview.entries.filter((entry) => entry.build && entry.selected).length} of {restorePreview.entries.filter((entry) => entry.build).length} valid runners selected</span>
            </div>
          </div>
        </div>
      )}

      {printOpen && (
        <div className="ab-modal-bg" onClick={() => setPrintOpen(false)}>
          <div className="ab-modal ab-print-dialog" role="dialog" aria-modal="true" aria-labelledby="print-title" onClick={(e) => e.stopPropagation()}>
            <div className="ab-modal-head">
              <span id="print-title"><Printer size={14} /> choose print sheets</span>
              <button type="button" className="ab-x" aria-label="Close print dialog" onClick={() => setPrintOpen(false)}><X size={16} /></button>
            </div>
            <fieldset className="ab-print-options">
              <legend>Print scope</legend>
              {PRINT_OPTIONS.map((option) => <label key={option.id} className={printScope === option.id ? "selected" : ""}>
                <input type="radio" name="print-scope" value={option.id} checked={printScope === option.id} onChange={() => setPrintScope(option.id)} />
                <span><b>{option.label}</b><small>{option.detail}</small></span>
              </label>)}
            </fieldset>
            <div className="ab-print-advice"><AlertTriangle size={14} /><span>In the browser print panel, turn off <b>Headers and footers</b>. Use the browser's page-range control only if you need something even more surgical.</span></div>
            <div className="ab-modal-foot">
              <Btn tone="gold" onClick={startPrint} title="Open browser print dialog"><Printer size={14} /> print selected</Btn>
              <Btn onClick={() => setPrintOpen(false)} title="Cancel printing">cancel</Btn>
            </div>
          </div>
        </div>
      )}

      <footer className="ab-legal">
        <b>Unofficial character builder and backup utility.</b> <a className="ab-text-link" href="./privacy.html" target="_blank" rel="noreferrer">Privacy and data use</a>. Shadowrun and associated marks are trademarks of The Topps Company, Inc.
        This utility is not affiliated with or endorsed by The Topps Company, Inc. or Catalyst Game Labs.
        Character data is saved in {storageProvider.label || "the configured storage provider"}. Runner Forge v{APP_VERSION}.
      </footer>
      </div>

      <PrintSheets build={build} cost={cost} level={level} remaining={remaining} phys={phys} ment={ment}
                   essence={essence} warnings={warnings} scope={printScope} career={career} careerActive={careerActive} />
    </div>
  );
}

const CostLine = ({ k, v, money = true }) => (
  <div className="ab-cost-line"><span>{k}</span><span>{money ? (v < 0 ? `−${yen(-v)}` : yen(v)) : v}</span></div>
);

// ---- Printable character sheets -----------------------------------------

const PrintList = ({ items, empty = "—" }) => items.length
  ? <ul className="ab-print-list">{items.map((item, i) => <li key={i}>{item}</li>)}</ul>
  : <p className="ab-print-empty">{empty}</p>;

const PrintSection = ({ title, children, className = "" }) => (
  <section className={`ab-print-section ${className}`.trim()}>
    <h2>{title}</h2>
    {children}
  </section>
);

// Keep narrative chunks below the measured one-page capacity. Browser print
// engines clip overfull multi-column boxes instead of reliably continuing them,
// so a conservative deterministic split protects the end of the backstory.
const paginateStory = (value, limit = 4400) => {
  const text = String(value || "No backstory entered.").trim();
  const paragraphs = text.split(/\n\s*\n/).map((part) => part.trim()).filter(Boolean);
  const pages = [];
  let current = "";
  const pushPiece = (piece) => {
    const candidate = current ? `${current}\n\n${piece}` : piece;
    if (candidate.length <= limit) { current = candidate; return; }
    if (current) { pages.push(current); current = ""; }
    if (piece.length <= limit) { current = piece; return; }
    const words = piece.split(/\s+/);
    let chunk = "";
    words.forEach((word) => {
      const next = chunk ? `${chunk} ${word}` : word;
      if (next.length > limit && chunk) { pages.push(chunk); chunk = word; }
      else chunk = next;
    });
    current = chunk;
  };
  paragraphs.forEach(pushPiece);
  if (current || !pages.length) pages.push(current || "No backstory entered.");
  return pages;
};

function PrintSheets({ build, cost, level, remaining, phys, ment, essence, warnings, scope, career, careerActive }) {
  const spellGroups = [...new Set(build.spells.map((spell) => spell.category))];
  const filled = (items) => items.map((item) => item.trim()).filter(Boolean);
  const armorTotal = build.armor.rating + build.armor.bonus;
  const storyPages = paginateStory(build.bio);
  return (
    <div className={`ab-print ab-print-scope-${scope}`} aria-hidden="true">
      <section className="ab-print-page ab-print-core-page">
        <header className="ab-print-head">
          <div><span>SHADOWRUN: ANARCHY 2.0</span><h1>RUNNER FORGE</h1></div>
          <div className="ab-print-sheet-label">CORE RUNNER SHEET</div>
        </header>

        <div className="ab-print-identity">
          <div><small>REAL NAME</small><b>{build.name || "—"}</b></div>
          <div><small>STREET HANDLE</small><b>{build.streetname || "—"}</b></div>
          <div><small>METATYPE</small><b>{build.metatype}</b></div>
          <div><small>LEVEL OF PLAY</small><b>{build.level}</b></div>
          <div className="wide"><small>AWAKENED TYPE</small><b>{build.awakenedType}</b></div>
          <div className="wide"><small>METATYPE / MAGIC NOTES</small><b>{METATYPES[build.metatype].quality} · {AWAKENED_TYPES[build.awakenedType].faculties}</b></div>
        </div>

        <div className="ab-print-core-grid">
          <div className="ab-print-stack">
            <PrintSection title="Attributes">
              <div className="ab-print-attrs">
                {ATTRS.map((key) => <div key={key}><small>{key}</small><b>{build.attributes[key]}</b></div>)}
              </div>
            </PrintSection>

            <PrintSection title="Condition Monitor">
              <table className="ab-print-table centered compact">
                <thead><tr><th></th><th>Light</th><th>Serious</th><th>Incap.</th></tr></thead>
                <tbody>
                  <tr><th>Physical</th>{phys.map((value, i) => <td key={i}>{value}</td>)}</tr>
                  <tr><th>Mental</th>{ment.map((value, i) => <td key={i}>{value}</td>)}</tr>
                </tbody>
              </table>
              <div className="ab-print-vitals"><span>Armor <b>{armorTotal}</b></span><span>Essence <b>{essence.toFixed(1)}</b></span><span>Edge <b>{build.attributes.EDGE}</b></span></div>
            </PrintSection>

            <PrintSection title="Risk Dice Reference">
              <table className="ab-print-table centered compact">
                <thead><tr><th>RR</th><th>Low</th><th>Normal</th><th>High</th><th>Extreme</th></tr></thead>
                <tbody>{RISK_TABLE.map((row, i) => <tr key={i}><th>{i}</th>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>)}</tbody>
              </table>
              <p className="ab-print-note">Risk-die hits count double. A rolled 1 glitches; Risk Reduction ignores that many 1s.</p>
            </PrintSection>

            <PrintSection title="Knowledge Skills">
              <PrintList items={build.knowledgeSkills} empty="No purchased Knowledge Skills" />
            </PrintSection>
          </div>

          <div className="ab-print-stack">
            <PrintSection title="Skills">
              <table className="ab-print-table skills">
                <thead><tr><th>Skill</th><th>R</th><th>Possible Attr.</th><th>Specializations</th></tr></thead>
                <tbody>{build.skills.length ? build.skills.map((skill) => <tr key={skill.name}>
                  <td>{skill.name}</td><td className="centered">{skill.rating}</td><td>{SKILLS[skill.name].attrs.join(" / ")}</td><td>{skill.specs.join(", ") || "—"}</td>
                </tr>) : <tr><td colSpan="4">No purchased Skills</td></tr>}</tbody>
              </table>
              <p className="ab-print-note">Dice pool = appropriate Attribute + Skill rating; a relevant specialization adds 2 dice.</p>
            </PrintSection>

            <div className="ab-print-narrative-grid">
              <PrintSection title="Keywords"><PrintList items={filled(build.keywords)} /></PrintSection>
              <PrintSection title="Dispositions"><PrintList items={filled(build.dispositions)} /></PrintSection>
              <PrintSection title="Cues" className="wide"><PrintList items={filled(build.cues).map((cue) => `“${cue}”`)} /></PrintSection>
            </div>
          </div>
        </div>
        <footer className="ab-print-footer"><span>Unofficial fan utility · Runner Forge v{APP_VERSION}</span><span>CORE RUNNER SHEET</span></footer>
      </section>

      <section className="ab-print-page ab-print-detail-page">
        <header className="ab-print-head">
          <div><span>{build.streetname || build.name || "RUNNER"}</span><h1>MAGIC, AMPS &amp; GEAR</h1></div>
          <div className="ab-print-sheet-label">DETAIL SHEET</div>
        </header>

        <div className="ab-print-two-col">
          <div className="ab-print-stack">
            <PrintSection title={`Magic · ${build.awakenedType}`}>
              <p className="ab-print-note lead">{AWAKENED_TYPES[build.awakenedType].faculties}</p>
              {spellGroups.length ? spellGroups.map((category) => <div className="ab-print-subgroup" key={category}>
                <h3>{category} Spells</h3><p>{build.spells.filter((spell) => spell.category === category).map((spell) => spell.name).join(" · ")}</p>
              </div>) : <p className="ab-print-empty">No spells</p>}
            </PrintSection>

            <PrintSection title="Adept Powers">
              {build.adeptPowers.length ? build.adeptPowers.map((power) => <div className="ab-print-item" key={power.name}>
                <b>{power.name} <small>R{power.rating}</small></b><span>{ADEPT_POWERS[power.name]?.effect || "—"}</span>
              </div>) : <p className="ab-print-empty">No adept powers</p>}
            </PrintSection>

          </div>

          <div className="ab-print-stack">
            <PrintSection title="Weapons">
              <table className="ab-print-table">
                <thead><tr><th>Weapon</th><th>DV</th><th>Range C/S/M/L</th><th>Expert</th></tr></thead>
                <tbody>{build.weapons.length ? build.weapons.map((weapon, i) => <tr key={i}><td>{weapon.name}</td><td>{weapon.dv}</td><td>{weapon.range}</td><td>{weapon.expert ? "Yes" : "—"}</td></tr>) : <tr><td colSpan="4">No weapons</td></tr>}</tbody>
              </table>
            </PrintSection>

            <PrintSection title="Armor & Gear">
              <div className="ab-print-item"><b>{build.armor.name || "No armor"}</b><span>Base {build.armor.rating} · stacking bonus {build.armor.bonus} · total {armorTotal}</span></div>
              {build.mentalProtection > 0 && <div className="ab-print-item"><b>Mental Protection</b><span>{build.mentalProtection}</span></div>}
              <PrintList items={["Commlink + trodes", "Fake SIN", ...build.gear]} />
            </PrintSection>

            <PrintSection title={careerActive ? "Career & Nuyen" : "Creation Summary"}>
              <table className="ab-print-table compact"><tbody>
                {careerActive ? <>
                  <tr><th>Available nuyen</th><td>{yen(career.balance)}</td></tr><tr><th>Completed jobs</th><td>{career.jobsCompleted}</td></tr>
                  <tr><th>Gross / expenses</th><td>{yen(career.gross)} / {yen(career.jobExpenses)}</td></tr><tr><th>Advancements</th><td>{yen(career.advancementSpent)}</td></tr>
                  <tr className="total"><th>Current character value</th><td>{yen(cost.total)}</td></tr>
                </> : <>
                  <tr><th>Attributes</th><td>{yen(cost.attrsC)}</td></tr><tr><th>Skills &amp; Knowledge</th><td>{yen(cost.skillsC)}</td></tr>
                  <tr><th>Magic</th><td>{yen(cost.magicC)}</td></tr><tr><th>Gear, weapons &amp; amps</th><td>{yen(cost.gearAllC)}</td></tr>
                  <tr className="total"><th>Total / {build.level} budget</th><td>{yen(cost.total)} / {yen(level.budget)}</td></tr>
                </>}
              </tbody></table>
              <p className="ab-print-note">{careerActive ? `Starting tier: ${build.level}. Progression uses earned nuyen, not a separate XP level.` : (remaining < 0 ? `${yen(-remaining)} over budget` : `${yen(remaining)} remaining`)}</p>
            </PrintSection>
          </div>
        </div>

        <PrintSection title="Shadow Amps" className="ab-print-full">
          <table className="ab-print-table amps">
            <thead><tr><th>Amp</th><th>Type</th><th>R</th><th>Ess.</th><th>Effects</th></tr></thead>
            <tbody>{build.amps.length ? build.amps.map((amp) => {
              const rr = (amp.rrEffects || []).map((effect) => `RR ${effect.level} ${effect.scope}: ${effect.target}`).join("; ");
              return <tr key={amp.id}><td>{amp.name}</td><td>{amp.type}</td><td>{amp.rating}</td><td>{amp.essence ? `−${amp.essence}` : "—"}</td><td>{[amp.effect, rr].filter(Boolean).join(" · ") || "—"}</td></tr>;
            }) : <tr><td colSpan="5">No Shadow Amps</td></tr>}</tbody>
          </table>
        </PrintSection>

        <PrintSection title="Creation Checks" className="ab-print-bottom">
          <PrintList items={warnings} empty="No automated creation checks are currently flagged." />
        </PrintSection>
        <footer className="ab-print-footer"><span>Shadowrun and associated marks are trademarks of The Topps Company, Inc. Not affiliated with or endorsed by Topps or Catalyst Game Labs.</span><span>RUNNER DETAIL SHEET</span></footer>
      </section>

      {storyPages.map((story, pageIndex) => {
        const last = pageIndex === storyPages.length - 1;
        return <section className="ab-print-page ab-print-story-page" key={pageIndex}>
          <header className="ab-print-head">
            <div><span>{build.streetname || build.name || "RUNNER"}</span><h1>{pageIndex ? "BACKSTORY CONTINUED" : "BACKSTORY & NOTES"}</h1></div>
            <div className="ab-print-sheet-label">NARRATIVE {pageIndex + 1} / {storyPages.length}</div>
          </header>
          <PrintSection title={pageIndex ? "Backstory - continued" : "Backstory"} className="ab-print-story-section">
            <div className="ab-print-backstory">{story}</div>
          </PrintSection>
          {last && <PrintSection title="Campaign & Character Notes" className="ab-print-story-notes">
            <div className="ab-print-writing-lines narrative">{Array.from({ length: 9 }, (_, i) => <span key={i} />)}</div>
          </PrintSection>}
          <footer className="ab-print-footer"><span>Unofficial fan utility · Runner Forge v{APP_VERSION}</span><span>NARRATIVE {pageIndex + 1} / {storyPages.length}</span></footer>
        </section>;
      })}
    </div>
  );
}

// ---- Attributes panel -----------------------------------------------------

function AttrPanel({ build, meta, setAttr }) {
  return (
    <section className="ab-panel">
      <h3 className="ab-h3"><Zap size={13} /> Attributes</h3>
      <div className="ab-attr-grid">
        {BUY_ATTRS.map((k) => {
          const max = meta[k];
          const v = build.attributes[k];
          const c = attrCost(v, max);
          return (
            <div key={k} className="ab-attr">
              <div className="ab-attr-name">{k}<em>{ATTR_LABEL[k]}</em></div>
              <div className="ab-stepper">
                <button type="button" aria-label={`Decrease ${ATTR_LABEL[k]}`} onClick={() => setAttr(k, Math.max(1, v - 1))} disabled={v <= 1}>−</button>
                <span className="ab-attr-val">{v}</span>
                <button type="button" aria-label={`Increase ${ATTR_LABEL[k]}`} onClick={() => setAttr(k, Math.min(max, v + 1))} disabled={v >= max}>+</button>
              </div>
              <div className="ab-attr-foot"><span>max {max}</span><span>{yen(c)}</span></div>
            </div>
          );
        })}
        <div className="ab-attr ab-attr-fixed-tile">
          <div className="ab-attr-name">EDGE<em>Edge</em></div>
          <div className="ab-attr-val ab-edge-val">{meta.EDGE}</div>
          <div className="ab-attr-foot"><span>metatype</span><span>free</span></div>
        </div>
      </div>
      <p className="ab-tiny">Attributes are 10,000¥ per point; the final point up to your metatype max costs 20,000¥. Everyone starts a build at 1s and buys up. Edge isn't bought — it's fixed by metatype (4 for humans, 3 for everyone else) and refreshes each session.</p>
    </section>
  );
}

// ---- Skills panel ---------------------------------------------------------

function SkillPanel({ build, setBuild }) {
  const [pick, setPick] = useState("");
  const [knowledge, setKnowledge] = useState("");
  const taken = build.skills.map((s) => s.name);
  const available = Object.keys(SKILLS).filter((s) => !taken.includes(s));

  const add = () => {
    if (!pick) return;
    const def = SKILLS[pick];
    setBuild((b) => ({ ...b, skills: [...b.skills, { name: pick, rating: 1, testAttr: def.attrs[0], specs: [] }] }));
    setPick("");
  };
  const addKnowledge = () => {
    const name = knowledge.trim(); if (!name) return;
    if (build.knowledgeSkills.some((x) => x.toLowerCase() === name.toLowerCase())) return;
    setBuild((b) => ({ ...b, knowledgeSkills: [...b.knowledgeSkills, name] })); setKnowledge("");
  };
  const update = (i, patch) => setBuild((b) => ({ ...b, skills: b.skills.map((s, ix) => ix === i ? { ...s, ...patch } : s) }));
  const remove = (i) => setBuild((b) => ({ ...b, skills: b.skills.filter((_, ix) => ix !== i) }));

  return (
    <section className="ab-panel">
      <h3 className="ab-h3">Skills</h3>
      <div className="ab-add-row">
        <div className="ab-select-wrap grow">
          <select aria-label="Skill to add" value={pick} onChange={(e) => setPick(e.target.value)}>
            <option value="">add a skill…</option>
            {available.map((s) => <option key={s} value={s}>{s}{SKILLS[s].awakened ? " (Awakened)" : ""}</option>)}
          </select><ChevronDown size={14} />
        </div>
        <Btn tone="gold" onClick={add} title="Add skill"><Plus size={14} /> add</Btn>
      </div>

      <div className="ab-skill-list">
        {build.skills.map((sk, i) => {
          const def = SKILLS[sk.name];
          const attrVal = build.attributes[sk.testAttr] || 0;
          const dp = sk.rating + attrVal;
          const openSpecs = def.specs.filter((sp) => !sk.specs.includes(sp));
          return (
            <div key={i} className="ab-skill">
              <div className="ab-skill-top">
                <span className="ab-skill-name">{sk.name}{def.note && <em> · {def.note}</em>}</span>
                <button type="button" className="ab-x" aria-label={`Remove ${sk.name}`} onClick={() => remove(i)}><X size={14} /></button>
              </div>
              <div className="ab-skill-body">
                <div className="ab-mini-step">
                  <button type="button" aria-label={`Decrease ${sk.name}`} onClick={() => update(i, { rating: Math.max(0, sk.rating - 1) })}>−</button>
                  <b>{sk.rating}</b>
                  <button type="button" aria-label={`Increase ${sk.name}`} onClick={() => update(i, { rating: Math.min(8, sk.rating + 1) })}>+</button>
                </div>
                {def.attrs.length > 1 ? (
                  <label className="ab-test-attr"><span>Test with</span><select className="ab-attr-sel" value={sk.testAttr} onChange={(e) => update(i, { testAttr: e.target.value })}>
                    {def.attrs.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select></label>
                ) : <span className="ab-attr-fixed">Test with {sk.testAttr}</span>}
                <span className="ab-dp">Pool <b>{dp}</b>{sk.specs.length ? <em> / {dp + 2} specialized</em> : null}</span>
              </div>
              <div className="ab-spec-row">
                {sk.specs.map((sp) => (
                  <span key={sp} className="ab-pill">{sp} <button type="button" aria-label={`Remove ${sp} specialization`} onClick={() => update(i, { specs: sk.specs.filter((x) => x !== sp) })}>×</button></span>
                ))}
                {openSpecs.length > 0 && (
                  <select className="ab-spec-add" aria-label={`Add a ${sk.name} specialization`} value="" onChange={(e) => e.target.value && update(i, { specs: [...sk.specs, e.target.value] })}>
                    <option value="">+ spec</option>
                    {openSpecs.map((sp) => <option key={sp} value={sp}>{sp}</option>)}
                  </select>
                )}
              </div>
            </div>
          );
        })}
        {build.skills.length === 0 && <p className="ab-empty">No skills yet. A runner wants at least Athletics, Stealth, Perception, Influence, and Network.</p>}
      </div>
      <p className="ab-tiny">“Test with” is a dice-pool calculator, not a permanent Skill pairing. Several Skills use different Attributes for different actions, and the GM can call for another sensible pairing.</p>

      <div className="ab-subsection">
        <h3 className="ab-h3">Knowledge Skills <span className="ab-cost-tag">2,500¥ each · no rating</span></h3>
        <div className="ab-add-row">
          <input className="grow" aria-label="Knowledge Skill name" placeholder="e.g. Seattle gangs, Sperethiel" value={knowledge}
                 onChange={(e) => setKnowledge(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addKnowledge()} />
          <Btn tone="gold" onClick={addKnowledge} title="Add Knowledge Skill"><Plus size={14} /> add</Btn>
        </div>
        <div className="ab-chip-list">
          {build.knowledgeSkills.map((name, i) => <span key={`${name}-${i}`} className="ab-chip">{name}<button type="button" aria-label={`Remove ${name}`} onClick={() => setBuild((b) => ({ ...b, knowledgeSkills: b.knowledgeSkills.filter((_, ix) => ix !== i) }))}><X size={12} /></button></span>)}
          {!build.knowledgeSkills.length && <p className="ab-empty">No purchased Knowledge Skills. Keywords and Skills at rating 4+ can also act as knowledge.</p>}
        </div>
      </div>
    </section>
  );
}

// ---- Magic panel ----------------------------------------------------------

function MagicPanel({ build, setBuild }) {
  const [cat, setCat] = useState("Combat");
  const [spellPick, setSpellPick] = useState("");
  const [custom, setCustom] = useState("");
  const [powerPick, setPowerPick] = useState("");

  const hasSpell = (name) => build.spells.some((s) => s.name.toLowerCase() === String(name).trim().toLowerCase());
  const addSpell = (name, category) => {
    const clean = String(name || "").trim(); if (!clean || hasSpell(clean)) return;
    setBuild((b) => ({ ...b, spells: [...b.spells, { name: clean, category }] }));
  };
  const addPower = () => {
    if (!powerPick || build.adeptPowers.some((p) => p.name === powerPick)) return;
    setBuild((b) => ({ ...b, adeptPowers: [...b.adeptPowers, { name: powerPick, rating: ADEPT_POWERS[powerPick].rating }] })); setPowerPick("");
  };
  const rmSpell = (i) => setBuild((b) => ({ ...b, spells: b.spells.filter((_, ix) => ix !== i) }));
  const rmPower = (i) => setBuild((b) => ({ ...b, adeptPowers: b.adeptPowers.filter((_, ix) => ix !== i) }));

  return (
    <>
      <section className="ab-panel">
        <h3 className="ab-h3">Spells <span className="ab-cost-tag">5,000¥ each</span></h3>
        <div className="ab-add-row">
          <div className="ab-select-wrap"><select aria-label="Spell category" value={cat} onChange={(e) => { setCat(e.target.value); setSpellPick(""); }}>
            {Object.keys(SPELLS).map((c) => <option key={c}>{c}</option>)}</select><ChevronDown size={14} /></div>
          <div className="ab-select-wrap grow"><select aria-label="Spell to add" value={spellPick} onChange={(e) => setSpellPick(e.target.value)}>
            <option value="">choose spell…</option>
            {SPELLS[cat].filter((s) => !hasSpell(s)).map((s) => <option key={s} value={s}>{s}</option>)}</select><ChevronDown size={14} /></div>
          <Btn tone="gold" title="Add spell" onClick={() => { addSpell(spellPick, cat); setSpellPick(""); }}><Plus size={14} /></Btn>
        </div>
        <div className="ab-add-row">
          <input className="grow" aria-label="Custom or reflavored spell name" placeholder="…or a custom / reflavored spell name" value={custom} onChange={(e) => setCustom(e.target.value)} />
          <Btn title="Add custom spell" onClick={() => { addSpell(custom, cat); setCustom(""); }}><Plus size={14} /> custom</Btn>
        </div>
        <div className="ab-chip-list">
          {build.spells.map((s, i) => (
            <span key={i} className="ab-chip"><em>{s.category}</em> {s.name}<button type="button" aria-label={`Remove ${s.name}`} onClick={() => rmSpell(i)}><X size={12} /></button></span>
          ))}
          {build.spells.length === 0 && <p className="ab-empty">No spells.</p>}
        </div>
      </section>

      <section className="ab-panel">
        <h3 className="ab-h3">Adept powers <span className="ab-cost-tag">rating × 5,000¥</span></h3>
        <div className="ab-add-row">
          <div className="ab-select-wrap grow"><select aria-label="Adept power to add" value={powerPick} onChange={(e) => setPowerPick(e.target.value)}>
            <option value="">add a power…</option>
            {Object.entries(ADEPT_POWERS).filter(([n]) => !build.adeptPowers.some((p) => p.name === n)).map(([n, d]) => <option key={n} value={n}>{n} — R{d.rating} · {yen(d.rating * 5000)}</option>)}
          </select><ChevronDown size={14} /></div>
          <Btn tone="gold" onClick={addPower} title="Add adept power"><Plus size={14} /> add</Btn>
        </div>
        {powerPick && <p className="ab-tiny ab-effect">{ADEPT_POWERS[powerPick].effect}</p>}
        <div className="ab-power-list">
          {build.adeptPowers.map((p, i) => (
            <div key={i} className="ab-power">
              <div><b>{p.name}</b><span className="ab-power-eff">{ADEPT_POWERS[p.name]?.effect}</span></div>
              <div className="ab-power-right"><span>R{p.rating} · {yen(p.rating * 5000)}</span><button type="button" aria-label={`Remove ${p.name}`} className="ab-x" onClick={() => rmPower(i)}><X size={14} /></button></div>
            </div>
          ))}
          {build.adeptPowers.length === 0 && <p className="ab-empty">No adept powers.</p>}
        </div>
      </section>
    </>
  );
}

// ---- Gear panel -----------------------------------------------------------

function AmpCard({ amp, allAmps, onUpdate, onRemove }) {
  const [scope, setScope] = useState("specialization");
  const [target, setTarget] = useState("");
  const [rrLevel, setRrLevel] = useState(1);
  const minRating = ampMinimumRating(amp, allAmps);
  const price = amp.rating * 5000 + (amp.baseCost || 0);
  const addRr = () => {
    const clean = target.trim(); if (!clean) return;
    onUpdate({ rrEffects: [...(amp.rrEffects || []), { id: makeId(), scope, target: clean, level: Number(rrLevel) }] });
    setTarget(""); setRrLevel(1);
  };
  return (
    <div className="ab-amp">
      <div className="ab-amp-head">
        <div><b>{amp.name}</b><span>{amp.type} · {yen(price)}{amp.essence ? ` · −${amp.essence} Essence` : ""}</span></div>
        <button type="button" className="ab-x" aria-label={`Remove ${amp.name}`} onClick={onRemove}><X size={14} /></button>
      </div>
      <div className="ab-amp-body">
        <label className="ab-inline-label"><span>Rating</span><span className="ab-mini-step">
          <button type="button" aria-label={`Decrease ${amp.name} rating`} onClick={() => onUpdate({ rating: Math.max(0, amp.rating - 1) })}>−</button>
          <b>{amp.rating}</b>
          <button type="button" aria-label={`Increase ${amp.name} rating`} onClick={() => onUpdate({ rating: Math.min(99, amp.rating + 1) })}>+</button>
        </span></label>
        <input className="grow" aria-label={`${amp.name} narrative or additional effects`} placeholder="narrative and other effects" value={amp.effect || ""} onChange={(e) => onUpdate({ effect: e.target.value })} />
      </div>
      <div className="ab-spec-row">
        {(amp.rrEffects || []).map((rr) => <span className="ab-pill" key={rr.id}>RR {rr.level} · {rr.scope}: {rr.target}
          <button type="button" aria-label={`Remove RR on ${rr.target}`} onClick={() => onUpdate({ rrEffects: amp.rrEffects.filter((x) => x.id !== rr.id) })}>×</button>
        </span>)}
      </div>
      <div className="ab-add-row ab-rr-add">
        <select aria-label="Risk Reduction scope" value={scope} onChange={(e) => setScope(e.target.value)}>
          <option value="specialization">specialization</option><option value="skill">Skill</option><option value="attribute">Attribute</option>
        </select>
        <input className="grow" aria-label="Risk Reduction target" placeholder="RR target" value={target} onChange={(e) => setTarget(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addRr()} />
        <select aria-label="Risk Reduction level" className="ab-rr-level" value={rrLevel} onChange={(e) => setRrLevel(Number(e.target.value))}>
          <option value="1">RR 1</option><option value="2">RR 2</option><option value="3">RR 3</option>
        </select>
        <Btn onClick={addRr} title="Add Risk Reduction effect"><Plus size={14} /> RR</Btn>
      </div>
      <p className="ab-tiny">{AMP_TYPES[amp.type].note} Structured effects require at least R{minRating}; include any other effects in the total rating yourself.</p>
    </div>
  );
}

function GearPanel({ build, setBuild, patch, essence }) {
  const [wName, setWName] = useState(""); const [wDv, setWDv] = useState("4"); const [wRange, setWRange] = useState("OK/—/—/—"); const [wExpert, setWExpert] = useState(false);
  const [gName, setGName] = useState("");
  const [aName, setAName] = useState(""); const [aType, setAType] = useState("Cyberware"); const [aRating, setARating] = useState(1); const [aEffect, setAEffect] = useState("");

  const defaultAmpRating = (type) => type === "Quality" ? (build.amps.some((a) => a.type === "Quality") ? 3 : 0) : AMP_TYPES[type].base;
  const chooseAmpType = (type) => { setAType(type); setARating(defaultAmpRating(type)); };
  const addWeapon = () => {
    if (!wName.trim() || !String(wDv).trim()) return;
    setBuild((b) => ({ ...b, weapons: [...b.weapons, { name: wName.trim(), dv: String(wDv).trim(), range: wRange.trim() || "OK/—/—/—", expert: wExpert }] }));
    setWName(""); setWDv("4"); setWRange("OK/—/—/—"); setWExpert(false);
  };
  const addGear = () => { const clean = gName.trim(); if (!clean) return; setBuild((b) => ({ ...b, gear: [...b.gear, clean] })); setGName(""); };
  const addAmp = () => {
    if (!aName.trim()) return;
    const def = AMP_TYPES[aType];
    setBuild((b) => ({ ...b, amps: [...b.amps, { id: makeId(), name: aName.trim(), type: aType, rating: clamp(aRating, 0, 99), essence: def.essence, baseCost: def.baseCost, effect: aEffect.trim(), rrEffects: [] }] }));
    setAName(""); setAEffect(""); setARating(aType === "Quality" ? 3 : AMP_TYPES[aType].base);
  };
  const rm = (key, i) => setBuild((b) => ({ ...b, [key]: b[key].filter((_, ix) => ix !== i) }));
  const updateAmp = (i, changes) => setBuild((b) => ({ ...b, amps: b.amps.map((a, ix) => ix === i ? { ...a, ...changes } : a) }));

  return (
    <>
      <section className="ab-panel">
        <h3 className="ab-h3">Weapons <span className="ab-cost-tag">2,500¥ · expert 5,000¥</span></h3>
        <div className="ab-add-row">
          <input className="grow" aria-label="Weapon name" placeholder="weapon name" value={wName} onChange={(e) => setWName(e.target.value)} />
          <input className="ab-dv" aria-label="Damage value" placeholder="DV, e.g. 5 or STR+2" value={wDv} onChange={(e) => setWDv(e.target.value)} />
          <input className="ab-range" aria-label="Range performance close short medium long" title="Close / short / medium / long" value={wRange} onChange={(e) => setWRange(e.target.value)} />
          <label className="ab-check"><input type="checkbox" checked={wExpert} onChange={(e) => setWExpert(e.target.checked)} /> expert</label>
          <Btn tone="gold" onClick={addWeapon} title="Add weapon"><Plus size={14} /></Btn>
        </div>
        <div className="ab-chip-list">
          {build.weapons.map((w, i) => <span key={i} className="ab-chip">{w.name} <em>DV {w.dv} · {w.range}{w.expert ? " · expert" : ""}</em><button type="button" aria-label={`Remove ${w.name}`} onClick={() => rm("weapons", i)}><X size={12} /></button></span>)}
        </div>
        <p className="ab-tiny">Range order is close / short / medium / long. Use OK, Dis., or —. Strength-based weapons can use values such as STR+1.</p>
      </section>

      <section className="ab-panel">
        <h3 className="ab-h3">Armor <span className="ab-cost-tag">2,500¥ per point</span></h3>
        <div className="ab-add-row">
          <input className="grow" aria-label="Armor name" placeholder="armor name (e.g. Lined Coat)" value={build.armor.name} onChange={(e) => patch({ armor: { ...build.armor, name: e.target.value } })} />
          <div className="ab-mini-step">
            <button type="button" aria-label="Decrease armor rating" onClick={() => patch({ armor: { ...build.armor, rating: Math.max(0, build.armor.rating - 1) } })}>−</button>
            <b>{build.armor.rating}</b>
            <button type="button" aria-label="Increase armor rating" onClick={() => patch({ armor: { ...build.armor, rating: Math.min(5, build.armor.rating + 1) } })}>+</button>
          </div>
        </div>
        <div className="ab-add-row">
          <label className="ab-inline-label"><span>Shield / Amp armor bonus</span><span className="ab-mini-step">
            <button type="button" aria-label="Decrease stacking armor bonus" onClick={() => patch({ armor: { ...build.armor, bonus: Math.max(0, build.armor.bonus - 1) } })}>−</button>
            <b>{build.armor.bonus}</b>
            <button type="button" aria-label="Increase stacking armor bonus" onClick={() => patch({ armor: { ...build.armor, bonus: Math.min(3, build.armor.bonus + 1) } })}>+</button>
          </span></label>
          <label className="ab-inline-label"><span>Mental Protection</span><input className="ab-num" type="number" min="0" max="20" value={build.mentalProtection} onChange={(e) => patch({ mentalProtection: clamp(e.target.value, 0, 20) })} /></label>
        </div>
        <p className="ab-tiny">Base armor is limited to 5. Shields and Amp armor can stack where allowed. Mental Protection is added to mental wound thresholds.</p>
      </section>

      <section className="ab-panel">
        <h3 className="ab-h3">Shadow Amps <span className="ab-cost-tag">typed · rating × 5,000¥</span></h3>
        <div className="ab-add-row">
          <input className="grow" aria-label="Shadow Amp name" placeholder="amp name" value={aName} onChange={(e) => setAName(e.target.value)} />
          <select aria-label="Shadow Amp type" value={aType} onChange={(e) => chooseAmpType(e.target.value)}>{Object.keys(AMP_TYPES).map((type) => <option key={type}>{type}</option>)}</select>
          <label className="ab-inline-label"><span>Rating</span><input className="ab-num" type="number" min="0" max="99" value={aRating} onChange={(e) => setARating(clamp(e.target.value, 0, 99))} /></label>
        </div>
        <div className="ab-add-row">
          <input className="grow" aria-label="Shadow Amp narrative effect" placeholder="narrative or additional effects" value={aEffect} onChange={(e) => setAEffect(e.target.value)} />
          <Btn tone="gold" onClick={addAmp} title="Add Shadow Amp"><Plus size={14} /> add</Btn>
        </div>
        <p className="ab-tiny ab-effect">{AMP_TYPES[aType].note} Essence and the underlying Equipment cost are assigned automatically.</p>
        <div className="ab-amp-list">
          {build.amps.map((amp, i) => <AmpCard key={amp.id} amp={amp} allAmps={build.amps} onUpdate={(changes) => updateAmp(i, changes)} onRemove={() => rm("amps", i)} />)}
          {!build.amps.length && <p className="ab-empty">No Shadow Amps.</p>}
        </div>
        <p className="ab-tiny">Essence now {essence.toFixed(1)} / 6.0. Cyberware costs 1 Essence per implant; bioware costs 0.5.</p>
      </section>

      <section className="ab-panel">
        <h3 className="ab-h3">Gear <span className="ab-cost-tag">2,500¥ each</span></h3>
        <div className="ab-add-row">
          <input className="grow" aria-label="Gear item" placeholder="item" value={gName} onChange={(e) => setGName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addGear()} />
          <Btn tone="gold" onClick={addGear} title="Add gear"><Plus size={14} /></Btn>
        </div>
        <div className="ab-chip-list">
          <span className="ab-chip ab-free">Commlink + trodes <em>free</em></span>
          <span className="ab-chip ab-free">Fake SIN <em>free</em></span>
          {build.gear.map((g, i) => <span key={i} className="ab-chip">{g}<button type="button" aria-label={`Remove ${g}`} onClick={() => rm("gear", i)}><X size={12} /></button></span>)}
        </div>
      </section>
    </>
  );
}

// ---- Career and advancement ----------------------------------------------

const localDate = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 10);
};

function CareerPanel({ build, setBuild, meta, career, announce }) {
  const [jobName, setJobName] = useState("");
  const [jobDate, setJobDate] = useState(localDate);
  const [jobClient, setJobClient] = useState("");
  const [jobPayout, setJobPayout] = useState("");
  const [jobExpenses, setJobExpenses] = useState("");
  const [jobNotes, setJobNotes] = useState("");
  const [advanceType, setAdvanceType] = useState("attribute");
  const [attributeTarget, setAttributeTarget] = useState("STR");
  const [skillTarget, setSkillTarget] = useState("Athletics");
  const [specializationSkill, setSpecializationSkill] = useState("");
  const [specializationTarget, setSpecializationTarget] = useState("");
  const [knowledgeName, setKnowledgeName] = useState("");
  const [spellCategory, setSpellCategory] = useState("Combat");
  const [spellName, setSpellName] = useState("");
  const [customSpell, setCustomSpell] = useState("");
  const [ampTarget, setAmpTarget] = useState("");
  const [gearName, setGearName] = useState("");
  const [weaponName, setWeaponName] = useState("");
  const [weaponDv, setWeaponDv] = useState("4");
  const [weaponRange, setWeaponRange] = useState("OK/—/—/—");
  const [weaponExpert, setWeaponExpert] = useState(false);
  const [customLabel, setCustomLabel] = useState("");
  const [customCost, setCustomCost] = useState("");
  const [customDetails, setCustomDetails] = useState("");
  const [adjustmentLabel, setAdjustmentLabel] = useState("");
  const [adjustmentAmount, setAdjustmentAmount] = useState("");

  const setCareer = (updater) => setBuild((current) => ({
    ...current,
    career: updater(current.career),
  }));

  const addJob = () => {
    const name = jobName.trim();
    const payout = Math.max(0, Math.round(Number(jobPayout) || 0));
    const expenses = Math.max(0, Math.round(Number(jobExpenses) || 0));
    if (!name) { announce("Give the completed job a name first."); return; }
    setCareer((current) => ({
      ...current,
      jobs: [...current.jobs, {
        id: makeId(), name, completedOn: jobDate, client: jobClient.trim(), payout, expenses, notes: jobNotes.trim(),
      }],
    }));
    setJobName(""); setJobClient(""); setJobPayout(""); setJobExpenses(""); setJobNotes("");
    announce(`${name} added to the job ledger.`);
  };

  const removeJob = (job) => {
    if (!window.confirm(`Remove ${job.name} and its nuyen from the ledger?`)) return;
    setCareer((current) => ({ ...current, jobs: current.jobs.filter((item) => item.id !== job.id) }));
  };

  const addAdjustment = () => {
    const amount = Math.round(Number(adjustmentAmount) || 0);
    if (!amount) { announce("Enter a positive or negative nuyen adjustment."); return; }
    const label = adjustmentLabel.trim() || (amount > 0 ? "Additional funds" : "Nuyen expense");
    setCareer((current) => ({
      ...current,
      adjustments: [...current.adjustments, { id: makeId(), recordedOn: localDate(), label, amount }],
    }));
    setAdjustmentLabel(""); setAdjustmentAmount("");
    announce("Nuyen adjustment recorded.");
  };

  const eligibleSpecSkills = build.skills.filter((skill) => skill.rating >= 1 && SKILLS[skill.name].specs.some((spec) => !skill.specs.includes(spec)));
  const chosenSpecSkill = eligibleSpecSkills.find((skill) => skill.name === specializationSkill) || eligibleSpecSkills[0];
  const availableSpecs = chosenSpecSkill ? SKILLS[chosenSpecSkill.name].specs.filter((spec) => !chosenSpecSkill.specs.includes(spec)) : [];
  const chosenSpec = availableSpecs.includes(specializationTarget) ? specializationTarget : availableSpecs[0] || "";
  const chosenAmp = build.amps.find((amp) => amp.id === ampTarget) || build.amps[0];
  const chosenSkill = build.skills.find((skill) => skill.name === skillTarget);
  const officialSpell = SPELLS[spellCategory].includes(spellName) ? spellName : "";

  const advancement = useMemo(() => {
    if (advanceType === "attribute") {
      const current = build.attributes[attributeTarget];
      const cost = nextAttributeCost(current, meta[attributeTarget]);
      return cost === null ? null : {
        type: "attribute", cost, label: `${ATTR_LABEL[attributeTarget]} ${current} → ${current + 1}`,
        details: current + 1 === meta[attributeTarget] ? "Final point to metatype maximum" : "Attribute increase",
        apply: (runner) => ({ ...runner, attributes: { ...runner.attributes, [attributeTarget]: current + 1 } }),
      };
    }
    if (advanceType === "skill") {
      const current = chosenSkill?.rating || 0;
      const cost = nextSkillCost(current);
      return cost === null ? null : {
        type: "skill", cost, label: `${skillTarget} ${current} → ${current + 1}`,
        details: chosenSkill ? "Skill increase" : "New Skill",
        apply: (runner) => chosenSkill
          ? ({ ...runner, skills: runner.skills.map((skill) => skill.name === skillTarget ? { ...skill, rating: current + 1 } : skill) })
          : ({ ...runner, skills: [...runner.skills, { name: skillTarget, rating: 1, testAttr: SKILLS[skillTarget].attrs[0], specs: [] }] }),
      };
    }
    if (advanceType === "specialization") return chosenSpecSkill && chosenSpec ? {
      type: "specialization", cost: 2500, label: `${chosenSpecSkill.name} (${chosenSpec})`, details: "New specialization",
      apply: (runner) => ({ ...runner, skills: runner.skills.map((skill) => skill.name === chosenSpecSkill.name ? { ...skill, specs: [...skill.specs, chosenSpec] } : skill) }),
    } : null;
    if (advanceType === "knowledge") {
      const name = knowledgeName.trim();
      if (!name || build.knowledgeSkills.some((item) => item.toLowerCase() === name.toLowerCase())) return null;
      return { type: "knowledge", cost: 2500, label: name, details: "New Knowledge Skill", apply: (runner) => ({ ...runner, knowledgeSkills: [...runner.knowledgeSkills, name] }) };
    }
    if (advanceType === "spell") {
      const name = (officialSpell || customSpell).trim();
      if (!name || build.spells.some((spell) => spell.name.toLowerCase() === name.toLowerCase())) return null;
      return { type: "spell", cost: 5000, label: name, details: `${spellCategory} spell`, apply: (runner) => ({ ...runner, spells: [...runner.spells, { name, category: spellCategory }] }) };
    }
    if (advanceType === "amp") return chosenAmp ? {
      type: "amp", cost: 5000, label: `${chosenAmp.name} R${chosenAmp.rating} → R${chosenAmp.rating + 1}`, details: "Shadow Amp rating increase; update its effect text as needed",
      apply: (runner) => ({ ...runner, amps: runner.amps.map((amp) => amp.id === chosenAmp.id ? { ...amp, rating: amp.rating + 1 } : amp) }),
    } : null;
    if (advanceType === "gear") {
      const name = gearName.trim();
      return name ? { type: "gear", cost: 2500, label: name, details: "Standard equipment", apply: (runner) => ({ ...runner, gear: [...runner.gear, name] }) } : null;
    }
    if (advanceType === "weapon") {
      const name = weaponName.trim();
      return name ? {
        type: "weapon", cost: weaponExpert ? 5000 : 2500, label: name, details: weaponExpert ? "Expert weapon" : "Standard weapon",
        apply: (runner) => ({ ...runner, weapons: [...runner.weapons, { name, dv: weaponDv.trim() || "4", range: weaponRange.trim() || "OK/—/—/—", expert: weaponExpert }] }),
      } : null;
    }
    if (advanceType === "armor") return build.armor.rating < 5 ? {
      type: "armor", cost: 2500, label: `Armor ${build.armor.rating} → ${build.armor.rating + 1}`, details: "Armor point",
      apply: (runner) => ({ ...runner, armor: { ...runner.armor, rating: runner.armor.rating + 1 } }),
    } : null;
    if (advanceType === "custom") {
      const label = customLabel.trim();
      const cost = Math.max(0, Math.round(Number(customCost) || 0));
      return label && cost ? { type: "custom", cost, label, details: customDetails.trim() || "GM-approved advancement or purchase", apply: (runner) => runner } : null;
    }
    return null;
  }, [advanceType, attributeTarget, build, meta, skillTarget, chosenSkill, chosenSpecSkill, chosenSpec, knowledgeName, spellCategory, officialSpell, customSpell, chosenAmp, gearName, weaponName, weaponDv, weaponRange, weaponExpert, customLabel, customCost, customDetails]);

  const buyAdvancement = () => {
    if (!advancement) { announce("Choose a valid advancement and target first."); return; }
    if (career.balance < advancement.cost) { announce(`That costs ${yen(advancement.cost)}; only ${yen(career.balance)} is available.`); return; }
    setBuild((runner) => {
      const advanced = advancement.apply(runner);
      return {
        ...advanced,
        career: {
          ...advanced.career,
          advancements: [...advanced.career.advancements, {
            id: makeId(), purchasedOn: localDate(), type: advancement.type, label: advancement.label,
            cost: advancement.cost, details: advancement.details,
          }],
        },
      };
    });
    if (advanceType === "knowledge") setKnowledgeName("");
    if (advanceType === "spell") { setSpellName(""); setCustomSpell(""); }
    if (advanceType === "gear") setGearName("");
    if (advanceType === "weapon") setWeaponName("");
    if (advanceType === "custom") { setCustomLabel(""); setCustomCost(""); setCustomDetails(""); }
    announce(`${advancement.label} purchased for ${yen(advancement.cost)}.`);
  };

  return (
    <>
      <section className="ab-panel">
        <h3 className="ab-h3">Career ledger <span className="ab-cost-tag">completed jobs fund advancement</span></h3>
        <div className="ab-career-summary">
          <div><small>AVAILABLE</small><b className={career.balance < 0 ? "over" : ""}>{yen(career.balance)}</b></div>
          <div><small>JOBS</small><b>{career.jobsCompleted}</b></div>
          <div><small>NET EARNED</small><b>{yen(career.netEarned)}</b></div>
          <div><small>INVESTED</small><b>{yen(career.advancementSpent)}</b></div>
        </div>
        <label className="ab-career-seed"><span>Starting nuyen on hand</span><input type="number" min="0" step="100" value={build.career.startingNuyen} onChange={(event) => setCareer((current) => ({ ...current, startingNuyen: Math.max(0, Math.round(Number(event.target.value) || 0)) }))} /></label>
        <p className="ab-tiny">Anarchy 2.0 uses nuyen for both equipment and character growth. The play level above is the runner's starting tier; it is not an XP ladder. Once this ledger has activity, creation-only caps stop producing warnings.</p>
      </section>

      <section className="ab-panel">
        <h3 className="ab-h3">Record completed job</h3>
        <div className="ab-job-form">
          <Field label="Job"><input value={jobName} onChange={(event) => setJobName(event.target.value)} placeholder="The Tacoma warehouse thing" /></Field>
          <Field label="Completed"><input type="date" value={jobDate} onChange={(event) => setJobDate(event.target.value)} /></Field>
          <Field label="Client / Johnson"><input value={jobClient} onChange={(event) => setJobClient(event.target.value)} placeholder="optional" /></Field>
          <Field label="Payout"><input type="number" min="0" step="100" value={jobPayout} onChange={(event) => setJobPayout(event.target.value)} placeholder="7500" /></Field>
          <Field label="Expenses"><input type="number" min="0" step="100" value={jobExpenses} onChange={(event) => setJobExpenses(event.target.value)} placeholder="0" /></Field>
        </div>
        <textarea rows="2" value={jobNotes} onChange={(event) => setJobNotes(event.target.value)} aria-label="Completed job notes" placeholder="Outcome, complications, who is still annoyed…" />
        <div className="ab-panel-actions"><Btn tone="gold" onClick={addJob} title="Add completed job and credit its net payout"><Plus size={14} /> complete job</Btn></div>
        <div className="ab-ledger-list">
          {[...build.career.jobs].reverse().map((job) => <div className="ab-ledger-row" key={job.id}>
            <div><b>{job.name}</b><span>{[job.completedOn, job.client].filter(Boolean).join(" · ") || "Completed job"}{job.notes ? ` · ${job.notes}` : ""}</span></div>
            <strong>{yen(job.payout - job.expenses)}</strong>
            <button type="button" className="ab-x" aria-label={`Remove ${job.name}`} onClick={() => removeJob(job)}><X size={14} /></button>
          </div>)}
          {!build.career.jobs.length && <p className="ab-empty">No completed jobs yet. The shadows remain stubbornly unpaid.</p>}
        </div>
      </section>

      <section className="ab-panel">
        <h3 className="ab-h3">Buy advancement <span className="ab-cost-tag">guided rules costs</span></h3>
        <div className="ab-add-row">
          <div className="ab-select-wrap grow"><select aria-label="Advancement type" value={advanceType} onChange={(event) => setAdvanceType(event.target.value)}>
            <option value="attribute">Attribute +1</option><option value="skill">Skill +1 / new Skill</option><option value="specialization">Specialization</option>
            <option value="knowledge">Knowledge Skill</option><option value="spell">Spell</option><option value="amp">Shadow Amp +1 rating</option>
            <option value="gear">Standard equipment</option><option value="weapon">Weapon</option><option value="armor">Armor +1</option><option value="custom">Custom / GM-approved</option>
          </select><ChevronDown size={14} /></div>
        </div>

        {advanceType === "attribute" && <div className="ab-add-row"><select aria-label="Attribute to improve" value={attributeTarget} onChange={(event) => setAttributeTarget(event.target.value)}>{BUY_ATTRS.map((key) => <option key={key} value={key}>{ATTR_LABEL[key]} {build.attributes[key]} / {meta[key]}</option>)}</select></div>}
        {advanceType === "skill" && <div className="ab-add-row"><select className="grow" aria-label="Skill to improve" value={skillTarget} onChange={(event) => setSkillTarget(event.target.value)}>{Object.keys(SKILLS).map((name) => <option key={name} value={name}>{name} {build.skills.find((skill) => skill.name === name)?.rating || 0} / 8</option>)}</select></div>}
        {advanceType === "specialization" && <div className="ab-add-row">
          <select className="grow" aria-label="Skill for specialization" value={chosenSpecSkill?.name || ""} onChange={(event) => { setSpecializationSkill(event.target.value); setSpecializationTarget(""); }}><option value="">choose Skill…</option>{eligibleSpecSkills.map((skill) => <option key={skill.name}>{skill.name}</option>)}</select>
          <select className="grow" aria-label="Specialization to buy" value={chosenSpec} onChange={(event) => setSpecializationTarget(event.target.value)}><option value="">choose specialization…</option>{availableSpecs.map((spec) => <option key={spec}>{spec}</option>)}</select>
        </div>}
        {advanceType === "knowledge" && <div className="ab-add-row"><input className="grow" aria-label="Knowledge Skill to buy" value={knowledgeName} onChange={(event) => setKnowledgeName(event.target.value)} placeholder="Knowledge Skill" /></div>}
        {advanceType === "spell" && <div className="ab-add-row">
          <select aria-label="Spell category to buy" value={spellCategory} onChange={(event) => { setSpellCategory(event.target.value); setSpellName(""); }}>{Object.keys(SPELLS).map((category) => <option key={category}>{category}</option>)}</select>
          <select className="grow" aria-label="Spell to buy" value={officialSpell} onChange={(event) => { setSpellName(event.target.value); setCustomSpell(""); }}><option value="">official spell…</option>{SPELLS[spellCategory].filter((name) => !build.spells.some((spell) => spell.name === name)).map((name) => <option key={name}>{name}</option>)}</select>
          <input className="grow" aria-label="Custom spell to buy" value={customSpell} onChange={(event) => { setCustomSpell(event.target.value); setSpellName(""); }} placeholder="or custom spell" />
        </div>}
        {advanceType === "amp" && <div className="ab-add-row"><select className="grow" aria-label="Shadow Amp to improve" value={chosenAmp?.id || ""} onChange={(event) => setAmpTarget(event.target.value)}><option value="">choose Amp…</option>{build.amps.map((amp) => <option key={amp.id} value={amp.id}>{amp.name} · R{amp.rating}</option>)}</select></div>}
        {advanceType === "gear" && <div className="ab-add-row"><input className="grow" aria-label="Equipment to buy" value={gearName} onChange={(event) => setGearName(event.target.value)} placeholder="equipment name" /></div>}
        {advanceType === "weapon" && <div className="ab-add-row"><input className="grow" aria-label="Weapon to buy" value={weaponName} onChange={(event) => setWeaponName(event.target.value)} placeholder="weapon name" /><input className="ab-dv" aria-label="Purchased weapon damage value" value={weaponDv} onChange={(event) => setWeaponDv(event.target.value)} /><input className="ab-range" aria-label="Purchased weapon range" value={weaponRange} onChange={(event) => setWeaponRange(event.target.value)} /><label className="ab-check"><input type="checkbox" checked={weaponExpert} onChange={(event) => setWeaponExpert(event.target.checked)} /> expert</label></div>}
        {advanceType === "armor" && <p className="ab-tiny ab-effect">Current base armor {build.armor.rating} / 5. This buys one armor point; shield and Amp bonuses remain separate.</p>}
        {advanceType === "custom" && <><div className="ab-add-row"><input className="grow" aria-label="Custom advancement name" value={customLabel} onChange={(event) => setCustomLabel(event.target.value)} placeholder="advancement or purchase" /><input className="ab-money" type="number" min="0" step="100" aria-label="Custom advancement cost" value={customCost} onChange={(event) => setCustomCost(event.target.value)} placeholder="cost" /></div><input aria-label="Custom advancement details" value={customDetails} onChange={(event) => setCustomDetails(event.target.value)} placeholder="GM ruling or details" /></>}

        <div className="ab-adv-preview">
          <span>{advancement ? advancement.label : "Choose a valid target"}</span>
          <b>{advancement ? yen(advancement.cost) : "—"}</b>
          <Btn tone="gold" onClick={buyAdvancement} title="Purchase advancement with available nuyen" disabled={!advancement || career.balance < advancement.cost}>buy</Btn>
        </div>
        <p className="ab-tiny">Use this panel for progression so the nuyen ledger and character stay in step. Direct edits on the other tabs remain available for corrections, losses, GM rulings, and the inevitable retroactive “we forgot the fake SIN” conversation.</p>

        <div className="ab-ledger-list">
          {[...build.career.advancements].reverse().map((item) => <div className="ab-ledger-row" key={item.id}><div><b>{item.label}</b><span>{item.purchasedOn || "Undated"} · {item.details}</span></div><strong>−{yen(item.cost)}</strong></div>)}
          {!build.career.advancements.length && <p className="ab-empty">No career advancements purchased.</p>}
        </div>
      </section>

      <section className="ab-panel">
        <h3 className="ab-h3">Other nuyen <span className="ab-cost-tag">lifestyle, loot, refunds, and GM fiat</span></h3>
        <div className="ab-add-row"><input className="grow" aria-label="Nuyen adjustment reason" value={adjustmentLabel} onChange={(event) => setAdjustmentLabel(event.target.value)} placeholder="reason" /><input className="ab-money" type="number" step="100" aria-label="Signed nuyen adjustment" value={adjustmentAmount} onChange={(event) => setAdjustmentAmount(event.target.value)} placeholder="+ / − amount" /><Btn onClick={addAdjustment} title="Record nuyen adjustment"><Plus size={14} /> record</Btn></div>
        <div className="ab-chip-list">{build.career.adjustments.map((item) => <span className="ab-chip" key={item.id}>{item.label} <em>{item.amount < 0 ? `−${yen(-item.amount)}` : `+${yen(item.amount)}`}</em><button type="button" aria-label={`Remove ${item.label}`} onClick={() => setCareer((current) => ({ ...current, adjustments: current.adjustments.filter((adjustment) => adjustment.id !== item.id) }))}><X size={12} /></button></span>)}</div>
      </section>
    </>
  );
}

// ---- Story panel ----------------------------------------------------------

function StoryPanel({ build, patch }) {
  const setArr = (key, i, v) => patch({ [key]: build[key].map((x, ix) => ix === i ? v : x) });
  return (
    <>
      <section className="ab-panel">
        <h3 className="ab-h3">Keywords <span className="ab-cost-tag">pick 5 · free</span></h3>
        {build.keywords.map((k, i) => <input key={i} className="ab-line" aria-label={`Keyword ${i + 1}`} placeholder={`keyword ${i + 1}`} value={k} onChange={(e) => setArr("keywords", i, e.target.value)} />)}
      </section>
      <section className="ab-panel">
        <h3 className="ab-h3">Dispositions <span className="ab-cost-tag">pick 4 · flaws earn Edge</span></h3>
        {build.dispositions.map((d, i) => <input key={i} className="ab-line" aria-label={`Disposition ${i + 1}`} placeholder={`disposition ${i + 1}`} value={d} onChange={(e) => setArr("dispositions", i, e.target.value)} />)}
      </section>
      <section className="ab-panel">
        <h3 className="ab-h3">Cues <span className="ab-cost-tag">pick 4 · things you actually say</span></h3>
        {build.cues.map((c, i) => <input key={i} className="ab-line" aria-label={`Cue ${i + 1}`} placeholder={`cue ${i + 1}`} value={c} onChange={(e) => setArr("cues", i, e.target.value)} />)}
      </section>
      <section className="ab-panel">
        <h3 className="ab-h3">Backstory</h3>
        <textarea className="ab-bio" aria-label="Character backstory" rows={6} value={build.bio} onChange={(e) => patch({ bio: e.target.value })} placeholder="Who are they, and why do they run the shadows?" />
      </section>
    </>
  );
}

// ---- Styles ---------------------------------------------------------------

const CSS = `
.ab-root{
  --bg:#0d0d10; --panel:#16161b; --panel2:#1c1c22; --line:#2c2c34;
  --gold:#f2c200; --gold-dim:#8a7315; --magenta:#ff2e88; --red:#e23b3b; --cyan:#38d6d6;
  --ink:#e9e7e1; --muted:#8f8f99;
  background:var(--bg); color:var(--ink); min-height:100vh;
  font-family:'JetBrains Mono',ui-monospace,monospace; padding:18px; box-sizing:border-box;
}
.ab-root *{box-sizing:border-box}
.ab-root select,.ab-root input,.ab-root textarea,.ab-root button{font-family:inherit}
.ab-root button:focus-visible,.ab-root input:focus-visible,.ab-root select:focus-visible,.ab-root textarea:focus-visible{outline:2px solid var(--cyan);outline-offset:2px}

/* masthead */
.ab-mast{display:flex;justify-content:space-between;align-items:flex-end;gap:16px;flex-wrap:wrap;
  border-bottom:2px solid var(--gold);padding-bottom:12px;margin-bottom:16px}
.ab-mast-title{display:flex;align-items:baseline;gap:10px;flex-wrap:wrap}
.ab-brand{font-family:'Oswald';font-weight:700;letter-spacing:.28em;font-size:15px;color:var(--muted)}
.ab-brand-2{font-family:'Oswald';font-weight:700;font-style:italic;letter-spacing:.04em;font-size:34px;
  color:var(--gold);text-transform:uppercase;transform:skewX(-6deg);text-shadow:2px 2px 0 #000}
.ab-brand-tag{font-size:10px;letter-spacing:.24em;text-transform:uppercase;color:var(--magenta)}
.ab-lib{display:flex;gap:8px;align-items:center;flex-wrap:wrap}
.ab-notice{min-height:20px;color:var(--cyan);font-size:11px;text-align:right;margin:-10px 0 6px}

/* buttons */
.ab-btn{display:inline-flex;align-items:center;gap:6px;border:1px solid var(--line);background:var(--panel2);
  color:var(--ink);padding:8px 11px;min-height:40px;font-size:11px;letter-spacing:.08em;text-transform:uppercase;cursor:pointer;
  border-radius:2px;transition:.12s}
.ab-btn:hover{border-color:var(--gold);color:var(--gold)}
.ab-btn:disabled{opacity:.45;cursor:not-allowed}
.ab-gold{background:var(--gold);color:#151109;border-color:var(--gold);font-weight:600}
.ab-gold:hover{background:#ffd21f;color:#151109}
.ab-danger:hover{border-color:var(--red);color:var(--red)}

/* layout */
.ab-grid{display:grid;grid-template-columns:1fr 320px;gap:16px;align-items:start}
.ab-side{position:sticky;top:18px;display:flex;flex-direction:column;gap:12px}
.ab-mobile-budget{display:none;background:var(--panel2);border:1px solid var(--line);padding:10px 12px;margin-bottom:12px;justify-content:space-between;gap:10px;font-size:11px;color:var(--muted)}
.ab-mobile-budget b{color:var(--gold)}.ab-mobile-budget.over b,.ab-mobile-budget.over span{color:var(--red)}
@media(max-width:860px){.ab-grid{grid-template-columns:1fr}.ab-side{position:static}.ab-mobile-budget{display:flex}.ab-mast{align-items:flex-start}.ab-lib{width:100%}}

/* panels */
.ab-panel{background:var(--panel);border:1px solid var(--line);border-radius:3px;padding:14px;margin-bottom:12px;
  position:relative}
.ab-panel::before{content:"";position:absolute;top:0;left:0;width:34px;height:3px;background:var(--gold)}
.ab-main .ab-panel:last-child{margin-bottom:0}
.ab-h3{font-family:'Oswald';font-weight:600;text-transform:uppercase;letter-spacing:.12em;font-size:14px;
  color:var(--gold);margin:0 0 12px;display:flex;align-items:center;gap:7px}
.ab-cost-tag{margin-left:auto;font-family:'JetBrains Mono';font-size:10px;color:var(--muted);letter-spacing:.04em;
  text-transform:none;font-weight:400}

/* identity */
.ab-id-row{display:flex;gap:10px;flex-wrap:wrap;margin-bottom:10px}
.ab-field{display:flex;flex-direction:column;gap:4px;flex:1;min-width:130px}
.ab-field span{font-size:10px;text-transform:uppercase;letter-spacing:.14em;color:var(--muted)}
.ab-root input,.ab-root select,.ab-root textarea{background:var(--bg);border:1px solid var(--line);color:var(--ink);
  padding:8px 9px;min-height:40px;font-size:12px;border-radius:2px;width:100%}
.ab-root input:focus,.ab-root select:focus,.ab-root textarea:focus{outline:none;border-color:var(--gold)}
.ab-meta-note{font-size:10px;color:var(--cyan);margin:4px 0 0;letter-spacing:.02em}

/* tabs */
.ab-tabs{display:flex;gap:2px;margin-bottom:12px;flex-wrap:wrap}
.ab-tab{flex:1;min-width:88px;background:var(--panel);border:1px solid var(--line);color:var(--muted);
  padding:10px;font-family:'Oswald';font-weight:600;text-transform:uppercase;letter-spacing:.1em;font-size:12px;
  cursor:pointer;border-radius:2px}
.ab-tab:hover{color:var(--ink)}
.ab-tab.on{background:var(--gold);color:#151109;border-color:var(--gold)}

/* attributes */
.ab-attr-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
@media(max-width:520px){.ab-attr-grid{grid-template-columns:repeat(2,1fr)}}
.ab-attr{background:var(--panel2);border:1px solid var(--line);border-radius:2px;padding:10px;text-align:center}
.ab-attr-name{font-family:'Oswald';font-weight:700;font-size:20px;color:var(--gold);line-height:1}
.ab-attr-name em{display:block;font-family:'JetBrains Mono';font-style:normal;font-size:10px;color:var(--muted);
  text-transform:uppercase;letter-spacing:.1em;margin-top:2px}
.ab-stepper{display:flex;align-items:center;justify-content:center;gap:8px;margin:8px 0}
.ab-stepper button,.ab-mini-step button{width:36px;height:36px;border:1px solid var(--line);background:var(--bg);
  color:var(--gold);font-size:16px;cursor:pointer;border-radius:2px;line-height:1}
.ab-stepper button:disabled{opacity:.3;cursor:not-allowed}
.ab-attr-val{font-family:'JetBrains Mono';font-weight:800;font-size:22px;min-width:22px}
.ab-attr-foot{display:flex;justify-content:space-between;font-size:10px;color:var(--muted)}
.ab-attr-fixed-tile{border-style:dashed;opacity:.9}
.ab-edge-val{display:block;margin:8px 0;font-size:22px;color:var(--magenta)}

/* add rows */
.ab-add-row{display:flex;gap:8px;margin-bottom:10px;align-items:center;flex-wrap:wrap}
.ab-add-row .grow,.grow{flex:1;min-width:120px}
.ab-num{width:64px}
.ab-dv{width:150px!important}.ab-range{width:170px!important}
.ab-select-wrap{position:relative;display:inline-flex;align-items:center}
.ab-select-wrap.grow{flex:1}
.ab-select-wrap select{appearance:none;padding-right:26px}
.ab-select-wrap svg{position:absolute;right:8px;pointer-events:none;color:var(--muted)}
.ab-check{display:inline-flex;align-items:center;gap:7px;font-size:11px;color:var(--muted);white-space:nowrap;min-height:40px}
.ab-check input{width:auto}

/* skills */
.ab-skill-list{display:flex;flex-direction:column;gap:8px}
.ab-skill{background:var(--panel2);border:1px solid var(--line);border-radius:2px;padding:10px}
.ab-skill-top{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px}
.ab-skill-name{font-family:'Oswald';font-weight:600;letter-spacing:.04em;font-size:14px}
.ab-skill-name em{font-style:normal;font-size:10px;color:var(--muted);font-family:'JetBrains Mono'}
.ab-skill-body{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
.ab-mini-step{display:inline-flex;align-items:center;gap:6px}
.ab-mini-step b{font-weight:800;min-width:14px;text-align:center}
.ab-test-attr,.ab-inline-label{display:inline-flex;align-items:center;gap:7px;color:var(--muted);font-size:10px;text-transform:uppercase;letter-spacing:.08em}
.ab-test-attr select,.ab-inline-label input{min-height:36px}
.ab-attr-sel{width:auto;padding:5px 7px}
.ab-attr-fixed{font-size:11px;color:var(--muted);border:1px solid var(--line);padding:5px 8px;border-radius:2px}
.ab-dp{margin-left:auto;font-size:11px;color:var(--muted);text-transform:uppercase;letter-spacing:.1em}
.ab-dp b{color:var(--cyan);font-size:16px;margin-left:4px}
.ab-dp em{font-style:normal;font-size:9px;letter-spacing:0;text-transform:none;color:var(--muted)}
.ab-spec-row{display:flex;gap:6px;flex-wrap:wrap;margin-top:9px;align-items:center}
.ab-pill{background:var(--bg);border:1px solid var(--gold-dim);color:var(--gold);font-size:10px;padding:3px 7px;border-radius:2px}
.ab-pill button{background:none;border:none;color:var(--gold);cursor:pointer;padding:0 0 0 3px;font-size:12px}
.ab-spec-add{width:auto;padding:4px 6px;font-size:10px}
.ab-subsection{border-top:1px solid var(--line);margin-top:16px;padding-top:14px}
.ab-x{background:none;border:none;color:var(--muted);cursor:pointer;display:flex;padding:2px}
.ab-x:hover{color:var(--red)}

/* chips */
.ab-chip-list{display:flex;gap:6px;flex-wrap:wrap;margin-top:4px}
.ab-chip{display:inline-flex;align-items:center;gap:5px;background:var(--panel2);border:1px solid var(--line);
  padding:5px 9px;border-radius:2px;font-size:11px}
.ab-chip em{font-style:normal;color:var(--muted);font-size:9px;text-transform:uppercase;letter-spacing:.06em}
.ab-chip button{background:none;border:none;color:var(--muted);cursor:pointer;display:flex;padding:0}
.ab-chip button:hover{color:var(--red)}
.ab-chip.ab-free{border-style:dashed;opacity:.7}

/* powers */
.ab-power-list{display:flex;flex-direction:column;gap:6px;margin-top:6px}
.ab-power{display:flex;justify-content:space-between;align-items:center;gap:10px;background:var(--panel2);
  border:1px solid var(--line);border-radius:2px;padding:9px 11px}
.ab-power b{font-family:'Oswald';font-weight:600;font-size:13px}
.ab-power-eff{display:block;font-size:10px;color:var(--muted);margin-top:2px}
.ab-power-right{display:flex;align-items:center;gap:8px;font-size:10px;color:var(--gold);white-space:nowrap}
.ab-effect{color:var(--cyan)!important}

/* Shadow Amps */
.ab-amp-list{display:flex;flex-direction:column;gap:9px;margin-top:10px}
.ab-amp{background:var(--panel2);border:1px solid var(--line);padding:11px;border-radius:2px}
.ab-amp-head{display:flex;justify-content:space-between;gap:10px;align-items:start}
.ab-amp-head b{font-family:'Oswald';font-size:14px;letter-spacing:.04em}.ab-amp-head span{display:block;color:var(--cyan);font-size:10px;margin-top:2px}
.ab-amp-body{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-top:9px}
.ab-rr-add{border-top:1px dashed var(--line);padding-top:9px;margin-top:9px}.ab-rr-add select{width:auto}.ab-rr-level{min-width:78px}

/* career */
.ab-career-summary{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:12px}
.ab-career-summary>div{background:var(--panel2);border:1px solid var(--line);padding:10px;text-align:center;min-width:0}
.ab-career-summary small{display:block;color:var(--muted);font-size:9px;letter-spacing:.1em;margin-bottom:4px}
.ab-career-summary b{display:block;color:var(--cyan);font-family:'Oswald';font-size:18px;overflow-wrap:anywhere}
.ab-career-summary b.over{color:var(--red)}
.ab-career-seed{display:flex;align-items:center;gap:10px;color:var(--muted);font-size:10px;text-transform:uppercase;letter-spacing:.08em}
.ab-career-seed input{width:180px;margin-left:auto}
.ab-job-form{display:grid;grid-template-columns:2fr 1fr 1.5fr 1fr 1fr;gap:8px;margin-bottom:8px}
.ab-panel-actions{display:flex;justify-content:flex-end;margin-top:8px}
.ab-ledger-list{display:flex;flex-direction:column;gap:6px;margin-top:12px;border-top:1px solid var(--line);padding-top:10px}
.ab-ledger-row{display:grid;grid-template-columns:1fr auto auto;align-items:center;gap:10px;background:var(--panel2);border:1px solid var(--line);padding:8px 10px}
.ab-ledger-row b{font-family:'Oswald';font-size:13px;letter-spacing:.03em}.ab-ledger-row span{display:block;color:var(--muted);font-size:9px;margin-top:2px;line-height:1.4}
.ab-ledger-row strong{color:var(--cyan);font-size:11px;white-space:nowrap}.ab-ledger-row strong:first-letter{color:var(--muted)}
.ab-adv-preview{display:flex;align-items:center;gap:10px;margin-top:10px;background:#211d0e;border:1px solid var(--gold-dim);padding:9px 10px}
.ab-adv-preview>span{flex:1;font-size:11px}.ab-adv-preview>b{color:var(--gold);white-space:nowrap}.ab-money{width:150px!important}
.ab-career-balance{text-align:center;border:1px solid var(--line);background:#101014;padding:10px;margin-top:4px}
.ab-career-balance b{display:block;color:var(--cyan);font-family:'Oswald';font-size:25px}.ab-career-balance span{display:block;color:var(--muted);font-size:9px;text-transform:uppercase;letter-spacing:.1em}
.ab-career-balance.over b{color:var(--red)}
@media(max-width:760px){.ab-job-form{grid-template-columns:1fr 1fr}.ab-job-form .ab-field:first-child{grid-column:1/-1}.ab-career-summary{grid-template-columns:1fr 1fr}}
@media(max-width:520px){.ab-job-form{grid-template-columns:1fr}.ab-job-form .ab-field:first-child{grid-column:auto}.ab-career-seed{align-items:stretch;flex-direction:column}.ab-career-seed input{width:100%;margin-left:0}.ab-ledger-row{grid-template-columns:1fr auto}.ab-ledger-row .ab-x{grid-column:2;grid-row:1}.ab-money{width:100%!important}}

/* story */
.ab-line{margin-bottom:7px}
.ab-bio{resize:vertical;line-height:1.5}

/* sidebar budget */
.ab-budget::before{background:var(--magenta)}
.ab-budget-head{display:flex;justify-content:space-between;align-items:baseline;font-family:'Oswald';
  text-transform:uppercase;letter-spacing:.12em;font-size:13px;color:var(--gold);margin-bottom:8px}
.ab-budget-head span:last-child{font-family:'JetBrains Mono';font-size:12px;font-weight:600;color:var(--ink);letter-spacing:0}
.ab-budget-head .ab-over{color:var(--red)}
.ab-meter{height:16px;background:#000;border:1px solid var(--line);border-radius:2px;overflow:hidden}
.ab-meter-fill{height:100%;background:repeating-linear-gradient(-45deg,var(--gold) 0 8px,#c9a200 8px 16px);transition:width .25s}
.ab-meter-fill.over{background:repeating-linear-gradient(-45deg,var(--red) 0 8px,#a02020 8px 16px)}
.ab-remaining{text-align:right;font-size:11px;margin-top:5px;color:var(--muted)}
.ab-remaining.over{color:var(--red);font-weight:600}
.ab-cost-lines{margin-top:10px;border-top:1px solid var(--line);padding-top:8px;display:flex;flex-direction:column;gap:4px}
.ab-cost-line{display:flex;justify-content:space-between;font-size:10px;color:var(--muted)}
.ab-cost-line span:last-child{color:var(--ink)}

/* warnings */
.ab-warn{border-color:var(--red)}
.ab-warn::before{background:var(--red)}
.ab-warn-head{display:flex;align-items:center;gap:6px;color:var(--red);font-family:'Oswald';font-weight:600;
  text-transform:uppercase;letter-spacing:.1em;font-size:12px;margin-bottom:6px}
.ab-warn ul{margin:0;padding-left:16px}
.ab-warn li{font-size:10px;color:#f0b4b4;margin-bottom:3px;line-height:1.4}

/* tables */
.ab-wound,.ab-risk{width:100%;border-collapse:collapse;font-size:11px}
.ab-wound th,.ab-wound td,.ab-risk th,.ab-risk td{border:1px solid var(--line);padding:5px;text-align:center}
.ab-wound th,.ab-risk th{color:var(--muted);font-weight:400;font-size:9px;text-transform:uppercase;letter-spacing:.06em}
.ab-wound td:first-child{text-align:left;color:var(--muted)}
.ab-wound tbody td{font-weight:800;color:var(--cyan)}
.ab-wound tbody td:first-child{color:var(--muted);font-weight:400}
.ab-risk .ab-rr{color:var(--gold);font-weight:800}
.ab-essence{margin-top:9px;font-size:10px;text-transform:uppercase;letter-spacing:.1em;color:var(--muted);display:flex;align-items:baseline;gap:6px}
.ab-essence b{color:var(--magenta);font-size:17px}
.ab-essence span{color:var(--muted)}

/* export modal */
.ab-modal-bg{position:fixed;inset:0;background:rgba(0,0,0,.72);display:flex;align-items:center;justify-content:center;
  padding:20px;z-index:50}
.ab-modal{background:var(--panel);border:1px solid var(--gold);border-radius:3px;width:min(680px,100%);
  max-height:86vh;display:flex;flex-direction:column;box-shadow:0 20px 60px rgba(0,0,0,.6)}
.ab-modal-head{display:flex;justify-content:space-between;align-items:center;padding:12px 14px;border-bottom:1px solid var(--line)}
.ab-modal-head span{display:flex;align-items:center;gap:7px;font-family:'Oswald';font-weight:600;text-transform:uppercase;
  letter-spacing:.08em;font-size:13px;color:var(--gold)}
.ab-modal-text{flex:1;min-height:320px;margin:0;border:none;border-radius:0;background:var(--bg);color:var(--ink);
  font-size:11px;line-height:1.55;padding:14px;resize:none}
.ab-modal-text:focus{outline:none;border:none}
.ab-modal-foot{display:flex;align-items:center;gap:10px;padding:12px 14px;border-top:1px solid var(--line);flex-wrap:wrap}
.ab-modal-foot .ab-tiny{margin:0}
.ab-export-options{border-bottom:1px solid var(--line)}
.ab-export-options label{align-items:flex-start}
.ab-export-options small{line-height:1.4}
.ab-restore-dialog{width:min(620px,100%)}
.ab-restore-summary{display:flex;flex-direction:column;gap:4px;padding:12px 14px;border-bottom:1px solid var(--line);font-size:10px;color:var(--muted);line-height:1.45}
.ab-restore-summary b{color:var(--ink);font-size:11px}
.ab-restore-tools{display:flex;gap:8px;padding:10px 14px 0}
.ab-restore-list{display:flex;flex-direction:column;gap:7px;padding:10px 14px 14px;overflow:auto;min-height:120px}
.ab-restore-list label{display:flex;align-items:flex-start;gap:10px;border:1px solid var(--line);background:var(--bg);padding:9px 10px;border-radius:2px;cursor:pointer}
.ab-restore-list label.selected{border-color:var(--cyan)}
.ab-restore-list label:has(input:disabled){cursor:not-allowed;opacity:.58}
.ab-restore-list input{width:auto;min-height:0;accent-color:var(--cyan);margin-top:2px}
.ab-restore-list span{display:flex;flex-direction:column;gap:2px}
.ab-restore-list b{font-size:11px;color:var(--ink)}
.ab-restore-list small{font-size:9px;color:var(--muted);line-height:1.4}
.ab-print-dialog{width:min(540px,100%)}
.ab-print-options{border:0;margin:0;padding:12px 14px;display:flex;flex-direction:column;gap:7px}
.ab-print-options legend{font-size:10px;text-transform:uppercase;letter-spacing:.12em;color:var(--muted);padding:0}
.ab-print-options label{display:flex;align-items:center;gap:10px;border:1px solid var(--line);background:var(--bg);padding:9px 10px;cursor:pointer;border-radius:2px}
.ab-print-options label.selected{border-color:var(--gold);background:#211d0e}
.ab-print-options input{width:auto;min-height:0;accent-color:var(--gold)}
.ab-print-options span{display:flex;flex-direction:column;gap:2px}.ab-print-options b{font-size:11px;color:var(--ink)}.ab-print-options small{font-size:9px;color:var(--muted)}
.ab-print-advice{display:flex;align-items:flex-start;gap:8px;margin:0 14px 12px;padding:9px 10px;border:1px solid var(--gold-dim);background:#211d0e;color:var(--muted);font-size:9px;line-height:1.45}
.ab-print-advice svg{flex:0 0 auto;color:var(--gold)}.ab-print-advice b{color:var(--ink)}
.ab-help-dialog{width:min(780px,100%)}
.ab-help-body{padding:14px;overflow:auto;display:flex;flex-direction:column;gap:18px}
.ab-help-body h3{display:flex;align-items:center;gap:7px;margin:0 0 10px;color:var(--gold);font-family:'Oswald';font-size:14px;text-transform:uppercase;letter-spacing:.1em}
.ab-help-controls{display:grid;grid-template-columns:1fr 1fr;gap:7px;margin:0}
.ab-help-controls>div{border:1px solid var(--line);background:var(--bg);padding:8px 10px}
.ab-help-controls dt{color:var(--cyan);font-size:10px;text-transform:uppercase;letter-spacing:.08em;font-weight:600}
.ab-help-controls dd{margin:3px 0 0;color:var(--muted);font-size:9px;line-height:1.45}
.ab-help-list{margin:0;padding-left:18px;color:var(--muted);font-size:10px;line-height:1.55}
.ab-help-list li{margin-bottom:5px}.ab-help-list code,.ab-help-note code,.ab-drive-body code{color:var(--cyan);font-size:.95em}
.ab-help-note{border-left:3px solid var(--cyan);background:var(--bg);padding:9px 11px;color:var(--muted);font-size:9px;line-height:1.5;margin:10px 0 0}
.ab-text-link{display:inline-flex;align-items:center;gap:5px;color:var(--cyan);font-size:10px;text-decoration:none}.ab-text-link:hover{color:var(--gold)}
.ab-drive-dialog{width:min(680px,100%)}
.ab-drive-body{padding:14px;overflow:auto;min-height:180px}
.ab-drive-status{display:flex;flex-direction:column;gap:5px;border:1px solid var(--cyan);background:#0d2225;padding:12px;color:var(--muted);font-size:10px;line-height:1.5}
.ab-drive-status>b,.ab-drive-status span>b{color:var(--ink);font-size:11px}.ab-drive-status.warn{border-color:var(--gold-dim);background:#211d0e}
.ab-drive-status.connected{flex-direction:row;align-items:flex-start}.ab-drive-status.connected svg{flex:0 0 auto;color:var(--cyan);margin-top:2px}.ab-drive-status.connected span{display:flex;flex-direction:column;gap:2px}.ab-drive-status em{font-style:normal;color:var(--gold)}
.ab-drive-auto{display:flex;align-items:flex-start;gap:10px;margin:10px 0 0;border:1px solid var(--line);background:var(--bg);padding:10px;cursor:pointer}.ab-drive-auto input{width:auto;min-height:0;margin-top:2px;accent-color:var(--gold)}.ab-drive-auto span{display:flex;flex-direction:column;gap:3px}.ab-drive-auto b{font-size:10px;color:var(--ink)}.ab-drive-auto small{font-size:9px;color:var(--muted);line-height:1.45}.ab-drive-auto em{color:var(--gold);font-style:normal}
.ab-drive-auto-state{margin:6px 1px 0;color:var(--cyan);font-size:9px;line-height:1.4}.ab-drive-auto-state.error{color:var(--red)}
.ab-drive-connect{display:flex;justify-content:center;padding:22px 0 8px}.ab-drive-actions{display:flex;gap:8px;flex-wrap:wrap;margin:12px 0}
.ab-drive-list{display:flex;flex-direction:column;gap:7px;border-top:1px solid var(--line);padding-top:10px;max-height:300px;overflow:auto}
.ab-drive-file{display:flex;align-items:center;gap:10px;background:var(--bg);border:1px solid var(--line);padding:8px 9px}.ab-drive-file>span{display:flex;flex:1;min-width:0;flex-direction:column;gap:2px}.ab-drive-file b{font-size:10px;overflow-wrap:anywhere}.ab-drive-file small{font-size:9px;color:var(--muted)}
@media(max-width:600px){.ab-help-controls{grid-template-columns:1fr}.ab-drive-actions{flex-direction:column}.ab-drive-actions .ab-btn{justify-content:center}.ab-drive-file{align-items:flex-start;flex-direction:column}.ab-drive-file .ab-btn{width:100%;justify-content:center}}

/* misc */
.ab-tiny{font-size:10px;color:var(--muted);line-height:1.5;margin:8px 0 0}
.ab-empty{font-size:11px;color:var(--muted);font-style:italic;margin:4px 0}
.ab-legal{border-top:1px solid var(--line);margin-top:18px;padding:14px 4px 4px;color:var(--muted);font-size:10px;line-height:1.6}
.ab-legal b{color:var(--ink)}
.ab-print{display:none}
@media(max-width:520px){.ab-root{padding:10px}.ab-brand-2{font-size:28px}.ab-btn{min-height:44px}.ab-stepper button,.ab-mini-step button{width:40px;height:40px}.ab-dv,.ab-range{width:100%!important}.ab-mobile-budget{flex-direction:column}}
@media(prefers-reduced-motion:reduce){.ab-root *{scroll-behavior:auto!important;transition:none!important;animation:none!important}}

@page{size:letter portrait;margin:.35in}
@media print{
  html,body,#root{margin:0!important;padding:0!important;background:#fff!important;color:#111!important}
  .ab-root{min-height:0!important;padding:0!important;background:#fff!important;color:#111!important}
  .ab-screen{display:none!important}
  .ab-print{display:block!important;font-family:'JetBrains Mono',ui-monospace,monospace;color:#111;font-size:8pt;line-height:1.28}
  .ab-print *{box-sizing:border-box;print-color-adjust:exact;-webkit-print-color-adjust:exact}
  .ab-print-page{display:none;position:relative;padding:0;background:#fff;break-after:page;page-break-after:always}
  .ab-print-scope-all .ab-print-page,.ab-print-scope-gameplay .ab-print-core-page,.ab-print-scope-gameplay .ab-print-detail-page,
  .ab-print-scope-core .ab-print-core-page,.ab-print-scope-detail .ab-print-detail-page,.ab-print-scope-story .ab-print-story-page{display:block}
  .ab-print-scope-all .ab-print-story-page,.ab-print-scope-story .ab-print-story-page{display:flex;flex-direction:column;min-height:9.7in}
  .ab-print-scope-all .ab-print-story-page:last-child,.ab-print-scope-gameplay .ab-print-detail-page,.ab-print-scope-core .ab-print-core-page,
  .ab-print-scope-detail .ab-print-detail-page,.ab-print-scope-story .ab-print-story-page:last-child{break-after:auto;page-break-after:auto}
  .ab-print-head{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #111;padding:0 0 8px;margin-bottom:8px}
  .ab-print-head span{display:block;font-family:'Oswald',sans-serif;font-size:8pt;font-weight:600;letter-spacing:.2em}
  .ab-print-head h1{font-family:'Oswald',sans-serif;font-size:22pt;font-style:italic;letter-spacing:.05em;line-height:1;margin:1px 0 0}
  .ab-print-head>div:first-child{min-width:0}.ab-print-sheet-label{flex:0 0 auto;margin-left:10px;font-family:'Oswald',sans-serif;font-size:9pt;font-weight:700;letter-spacing:.08em;text-align:right;white-space:nowrap}
  .ab-print-identity{display:grid;grid-template-columns:repeat(4,1fr);border:1px solid #111;margin-bottom:8px}
  .ab-print-identity>div{min-height:34px;padding:4px 6px;border-right:1px solid #aaa;border-bottom:1px solid #aaa}
  .ab-print-identity>div:nth-child(4),.ab-print-identity>div:last-child{border-right:0}
  .ab-print-identity>div:nth-last-child(-n+2){border-bottom:0}
  .ab-print-identity .wide{grid-column:span 2}
  .ab-print-identity small{display:block;font-size:5.8pt;font-weight:600;letter-spacing:.1em;color:#444;margin-bottom:2px}
  .ab-print-identity b{display:block;font-size:8pt}
  .ab-print-core-grid{display:grid;grid-template-columns:31% 1fr;gap:8px}
  .ab-print-two-col{display:grid;grid-template-columns:1fr 1fr;gap:8px}
  .ab-print-stack{display:flex;flex-direction:column;gap:8px;min-width:0}
  .ab-print-section{border:1px solid #222;break-inside:avoid;page-break-inside:avoid}
  .ab-print-section>h2{font-family:'Oswald',sans-serif;font-size:10pt;text-transform:uppercase;letter-spacing:.1em;line-height:1.1;margin:0;padding:4px 6px;background:#222;color:#fff}
  .ab-print-section>p,.ab-print-section>div,.ab-print-section>ul{margin:5px 6px}
  .ab-print-attrs{display:grid!important;grid-template-columns:repeat(3,1fr);gap:4px}
  .ab-print-attrs>div{border:1px solid #777;text-align:center;padding:4px 2px}
  .ab-print-attrs small{display:block;font-size:6pt;font-weight:700;letter-spacing:.1em}
  .ab-print-attrs b{display:block;font-family:'Oswald',sans-serif;font-size:17pt;line-height:1}
  .ab-print-table{width:100%;border-collapse:collapse;font-size:7pt;line-height:1.25}
  .ab-print-table th,.ab-print-table td{border:1px solid #aaa;padding:3px 4px;text-align:left;vertical-align:top}
  .ab-print-table thead th{background:#e8e8e8;font-size:6.2pt;text-transform:uppercase;letter-spacing:.04em}
  .ab-print-table.compact th,.ab-print-table.compact td{padding:2px 3px}
  .ab-print-table.centered th,.ab-print-table.centered td,.ab-print-table .centered{text-align:center}
  .ab-print-table.skills td:first-child{font-weight:700;white-space:nowrap}
  .ab-print-table.amps th:nth-child(1){width:18%}.ab-print-table.amps th:nth-child(2){width:14%}.ab-print-table.amps th:nth-child(3),.ab-print-table.amps th:nth-child(4){width:5%;text-align:center}
  .ab-print-table .total th,.ab-print-table .total td{font-weight:800;border-top:2px solid #111}
  .ab-print-vitals{display:flex!important;justify-content:space-around;gap:4px;text-transform:uppercase;font-size:6.5pt}
  .ab-print-vitals b{font-size:10pt;margin-left:2px}
  .ab-print-note{font-size:6.5pt;color:#333}.ab-print-note.lead{font-size:7pt}
  .ab-print-list{padding-left:16px;margin-top:4px!important;margin-bottom:4px!important}
  .ab-print-list li{margin-bottom:2px}
  .ab-print-empty{color:#666;font-style:italic}
  .ab-print-narrative-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px}
  .ab-print-narrative-grid .wide{grid-column:1/-1}
  .ab-print-subgroup{margin-bottom:5px!important}.ab-print-subgroup h3{font-family:'Oswald',sans-serif;font-size:8pt;text-transform:uppercase;margin:0 0 1px}.ab-print-subgroup p{margin:0}
  .ab-print-item{display:flex!important;flex-direction:column;gap:1px;border-bottom:1px dotted #888;padding-bottom:4px;margin-bottom:4px!important}
  .ab-print-item:last-child{border-bottom:0}.ab-print-item b small{font-size:6pt}.ab-print-item span{font-size:6.5pt;color:#333}
  .ab-print-story-section{break-inside:auto;page-break-inside:auto}
  .ab-print-backstory{white-space:pre-wrap;column-count:2;column-gap:.25in;column-rule:1px solid #bbb;font-size:7.2pt;line-height:1.34;orphans:3;widows:3}
  .ab-print-full{margin-top:8px}
  .ab-print-bottom{margin-top:8px}
  .ab-print-writing-lines{height:1.05in!important;margin:5px 6px!important;display:flex!important;flex-direction:column;justify-content:space-between}
  .ab-print-story-notes{margin-top:8px;min-height:1.25in;flex:1;display:flex;flex-direction:column}
  .ab-print-writing-lines.narrative{height:auto!important;min-height:1.05in;flex:1}
  .ab-print-writing-lines span{display:block;border-bottom:1px solid #aaa;height:1px}
  .ab-print-footer{display:flex;justify-content:space-between;gap:12px;border-top:1px solid #111;margin-top:8px;padding-top:4px;font-size:5.6pt;color:#444}
  .ab-print-story-page .ab-print-footer{margin-top:auto}
  .ab-print-footer span:last-child{font-family:'Oswald',sans-serif;font-weight:700;letter-spacing:.1em;white-space:nowrap}
}
`;


createRoot(document.getElementById("root")).render(<AnarchyBuilder />);
